import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, companyAPI, workersAPI, teamAPI, clientsAPI, expensesAPI, bookkeepingAPI, authAPI, adminAPI, utilsAPI } from '../lib/api';
import { exportReportPDF, exportAnalysisPDF, exportExcel, exportCSV } from '../lib/exportUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  House,
  Buildings,
  CurrencyDollar,
  TrendUp,
  TrendDown,
  Bank,
  Plus,
  SignOut,
  User,
  Gear,
  CreditCard,
  CaretRight,
  ChartLineUp,
  Wallet,
  Receipt,
  Bell,
  MagnifyingGlass,
  List,
  CaretDown,
  ArrowRight,
  CalendarBlank,
  FileText,
  Sparkle,
  Question,
  Invoice,
  Money,
  ArrowsLeftRight,
  Users,
  ChartBar,
  Notebook,
  Calculator,
  Coin,
  X,
  Check,
  Clock,
  Warning,
  Eye,
  Trash,
  PencilSimple,
  Export,
  FunnelSimple,
  SortAscending,
  CurrencyCircleDollar,
  ArrowsClockwise,
  Globe,
  CurrencyEur,
  CurrencyGbp,
  UserCirclePlus,
  UsersThree,
  Briefcase,
  EnvelopeSimple,
  UserPlus,
  Gift,
  Percent,
  Crown,
  CaretLeft,
  MapPin,
  Phone,
  Envelope,
  NotePencil,
  BookOpen,
  CloudArrowUp,
  File,
  FilePlus,
  Scales,
  ListChecks,
  Table,
  DownloadSimple,
  Moon,
  Sun,
  LockKey,
  UserCircle,
  Palette,
  ShieldCheck
} from '@phosphor-icons/react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';
import AddCompanyModal from '../components/AddCompanyModal';

// Logo component
const FinanciALLogo = () => (
  <img 
    src="https://customer-assets-0z36b82j.emergentagent.net/job_1ce9899f-22e9-4951-9d4b-a91c40afd391/artifacts/ktwxigwh_Liquid%20Glass%20Logo.png.png" 
    alt="FinanciAL" 
    className="w-8 h-8 rounded-lg object-cover"
  />
);

// Currency configurations
const CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  EUR: { symbol: '€', name: 'Euro', flag: '🇪🇺' },
  GBP: { symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  ALL: { symbol: 'L', name: 'Albanian Lek', flag: '🇦🇱' },
  CHF: { symbol: 'Fr', name: 'Swiss Franc', flag: '🇨🇭' },
  JPY: { symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', flag: '🇨🇦' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', flag: '🇦🇺' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳' },
  INR: { symbol: '₹', name: 'Indian Rupee', flag: '🇮🇳' },
  TRY: { symbol: '₺', name: 'Turkish Lira', flag: '🇹🇷' },
  RSD: { symbol: 'дин', name: 'Serbian Dinar', flag: '🇷🇸' },
  MKD: { symbol: 'ден', name: 'Macedonian Denar', flag: '🇲🇰' },
  BGN: { symbol: 'лв', name: 'Bulgarian Lev', flag: '🇧🇬' },
  RON: { symbol: 'lei', name: 'Romanian Leu', flag: '🇷🇴' },
  HRK: { symbol: 'kn', name: 'Croatian Kuna', flag: '🇭🇷' },
  PLN: { symbol: 'zł', name: 'Polish Zloty', flag: '🇵🇱' },
  CZK: { symbol: 'Kč', name: 'Czech Koruna', flag: '🇨🇿' },
  HUF: { symbol: 'Ft', name: 'Hungarian Forint', flag: '🇭🇺' },
  SEK: { symbol: 'kr', name: 'Swedish Krona', flag: '🇸🇪' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone', flag: '🇳🇴' },
  DKK: { symbol: 'kr', name: 'Danish Krone', flag: '🇩🇰' },
};

// Sidebar Navigation Item
const NavItem = ({ icon: Icon, label, active, onClick, badge, collapsed }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
      active 
        ? 'bg-[#1A3C34] text-white' 
        : 'text-[#1A3C34]/70 hover:bg-[#1A3C34]/5 hover:text-[#1A3C34]'
    }`}
    title={collapsed ? label : undefined}
  >
    <Icon className="w-5 h-5 flex-shrink-0" weight={active ? 'fill' : 'regular'} />
    {!collapsed && (
      <>
        <span className="flex-1 text-left">{label}</span>
        {badge && (
          <span className="px-2 py-0.5 text-xs rounded-full bg-[#D4A373] text-white">
            {badge}
          </span>
        )}
      </>
    )}
  </button>
);

// Quick Stat Card
const QuickStat = ({ label, value, change, icon: Icon, color = 'default' }) => {
  const colorClasses = {
    default: 'bg-[#1A3C34]/5 text-[#1A3C34]',
    success: 'bg-[#2A9D8F]/10 text-[#2A9D8F]',
    warning: 'bg-[#D4A373]/10 text-[#D4A373]',
    danger: 'bg-[#E76F51]/10 text-[#E76F51]'
  };

  return (
    <div className="bg-white rounded-xl p-5 border border-[#1A3C34]/5 hover:border-[#1A3C34]/10 transition-all hover:shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#1A3C34]/60 text-sm">{label}</span>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" weight="duotone" />
        </div>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-semibold text-[#1A3C34] font-mono">{value}</span>
        {change !== undefined && (
          <span className={`text-xs font-medium flex items-center gap-0.5 ${
            change >= 0 ? 'text-[#2A9D8F]' : 'text-[#E76F51]'
          }`}>
            {change >= 0 ? <TrendUp className="w-3 h-3" /> : <TrendDown className="w-3 h-3" />}
            {Math.abs(change).toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
};

// Company Card
const CompanyCard = ({ company, onClick, formatCurrency }) => (
  <motion.div
    initial={{ opacity: 0, y: 5 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -2 }}
    onClick={onClick}
    className="bg-white rounded-xl p-4 border border-[#1A3C34]/5 hover:border-[#1A3C34]/15 transition-all cursor-pointer group"
  >
    <div className="flex items-center gap-4">
      <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-semibold text-lg">
        {company.name?.charAt(0) || 'K'}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-[#1A3C34] truncate">{company.name}</h3>
        <p className="text-xs text-[#1A3C34]/50">{company.industry}</p>
      </div>
      <div className="text-right">
        <p className="font-mono text-sm font-medium text-[#1A3C34]">
          {formatCurrency(company.revenue)}
        </p>
        <p className="text-xs text-[#1A3C34]/50">Të ardhura</p>
      </div>
      <CaretRight className="w-4 h-4 text-[#1A3C34]/30 group-hover:text-[#1A3C34]/60 transition-colors" />
    </div>
  </motion.div>
);

// Empty State
const EmptyState = ({ icon: Icon, title, description, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6">
    <div className="w-16 h-16 rounded-2xl bg-[#1A3C34]/5 flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-[#1A3C34]/40" />
    </div>
    <h3 className="text-lg font-medium text-[#1A3C34] mb-2">{title}</h3>
    <p className="text-[#1A3C34]/60 text-sm text-center max-w-sm mb-6">{description}</p>
    {actionLabel && (
      <Button onClick={onAction} className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white rounded-lg px-6">
        <Plus className="w-4 h-4 mr-2" />
        {actionLabel}
      </Button>
    )}
  </div>
);

// Status Badge
const StatusBadge = ({ status }) => {
  const config = {
    paid: { bg: 'bg-[#2A9D8F]/10', text: 'text-[#2A9D8F]', label: 'Paguar' },
    pending: { bg: 'bg-[#D4A373]/10', text: 'text-[#D4A373]', label: 'Në pritje' },
    overdue: { bg: 'bg-[#E76F51]/10', text: 'text-[#E76F51]', label: 'Vonuar' },
    draft: { bg: 'bg-[#1A3C34]/10', text: 'text-[#1A3C34]', label: 'Draft' },
    approved: { bg: 'bg-[#2A9D8F]/10', text: 'text-[#2A9D8F]', label: 'Aprovuar' },
    rejected: { bg: 'bg-[#E76F51]/10', text: 'text-[#E76F51]', label: 'Refuzuar' }
  };
  const c = config[status] || config.pending;
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// ===== MAIN DASHBOARD COMPONENT =====
const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, updateUserData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeNav, setActiveNav] = useState('dashboard');
  
  // Currency state
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem('preferredCurrency') || 'USD';
  });
  const [exchangeRates, setExchangeRates] = useState({});
  const [ratesLoading, setRatesLoading] = useState(true);
  const [lastRateUpdate, setLastRateUpdate] = useState(null);
  
  // Currency exchanger state
  const [exchangeFrom, setExchangeFrom] = useState('USD');
  const [exchangeTo, setExchangeTo] = useState('EUR');
  const [exchangeAmount, setExchangeAmount] = useState('1000');
  const [convertedAmount, setConvertedAmount] = useState(null);
  
  // Modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showCurrencySettings, setShowCurrencySettings] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsName, setSettingsName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [changingPw, setChangingPw] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('financial_dark_mode') === 'true');
  const [notifEmail, setNotifEmail] = useState(() => localStorage.getItem('financial_notif_email') !== 'false');
  const [notifProduct, setNotifProduct] = useState(() => localStorage.getItem('financial_notif_product') === 'true');
  
  // 2FA State
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFASetupId, setTwoFASetupId] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [showTwoFAVerify, setShowTwoFAVerify] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Data states for each section
  const [invoices, setInvoices] = useState([]);

  const [expenses, setExpenses] = useState([]);

  const [transactions, setTransactions] = useState([]);

  const [clients, setClients] = useState([]);

  // Report modal state
  const [activeReport, setActiveReport] = useState(null);

  // Form states
  const [newInvoice, setNewInvoice] = useState({ client: '', amount: '', dueDate: '', description: '' });
  const [newExpense, setNewExpense] = useState({ description: '', category: 'supplies', amount: '', date: '' });
  const [newTransaction, setNewTransaction] = useState({ description: '', type: 'income', amount: '', account: 'Llogari Bankare' });

  // Workers state
  const [workers, setWorkers] = useState([]);
  const [workersStats, setWorkersStats] = useState(null);
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [newWorker, setNewWorker] = useState({ name: '', role: '', monthly_wage: '', bonus: 0, hire_date: '', notes: '', client_id: null });

  // Team state
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({ email: '', role: 'viewer' });

  // Client detail state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [showClientInvoiceModal, setShowClientInvoiceModal] = useState(false);
  const [showClientExpenseModal, setShowClientExpenseModal] = useState(false);
  const [newClientData, setNewClientData] = useState({ name: '', email: '', phone: '', address: '', notes: '' });
  const [newClientInvoice, setNewClientInvoice] = useState({ amount: '', description: '', due_date: '', status: 'pending' });
  const [newClientExpense, setNewClientExpense] = useState({ description: '', category: 'supplies', amount: '', date: '' });
  const [clientsFromDB, setClientsFromDB] = useState([]);

  // Book-keeping state
  const [bookkeepingStats, setBookkeepingStats] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [bookkeepingDocuments, setBookkeepingDocuments] = useState([]);
  const [trialBalance, setTrialBalance] = useState(null);
  const [bookkeepingView, setBookkeepingView] = useState('overview'); // overview, ledger, journal, documents, trial-balance
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [newLedgerEntry, setNewLedgerEntry] = useState({ account_name: '', account_type: 'expense', debit: '', credit: '', description: '', reference: '', date: '' });
  const [newJournalEntry, setNewJournalEntry] = useState({ description: '', date: '', reference: '', entries: [{ account: '', type: 'expense', debit: '', credit: '' }, { account: '', type: 'expense', debit: '', credit: '' }] });
  const [newDocument, setNewDocument] = useState({ name: '', document_type: 'invoice', notes: '', file: null });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [analyzingBookkeeping, setAnalyzingBookkeeping] = useState(false);
  const [bookkeepingAnalyses, setBookkeepingAnalyses] = useState([]);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  // Cloud documents view state
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentFilter, setDocumentFilter] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null); // { name, file_type, dataUrl }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Fetch exchange rates from Frankfurter API
  const fetchExchangeRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      // Fetch rates via backend proxy (avoids browser CORS)
      const response = await utilsAPI.getExchangeRates();
      const data = response.data;
      const rates = { USD: 1, ...data.rates };
      if (!rates.ALL) {
        rates.ALL = 95.5;
      }
      setExchangeRates(rates);
      if (data.date) setLastRateUpdate(new Date(data.date));
    } catch (error) {
      // Fallback rates if API fails
      setExchangeRates({
        USD: 1, EUR: 0.92, GBP: 0.79, ALL: 95.5, CHF: 0.88,
        JPY: 149.5, CAD: 1.36, AUD: 1.53, CNY: 7.24, INR: 83.12,
        TRY: 30.25, RSD: 108.5, MKD: 56.8, BGN: 1.80, RON: 4.57,
        PLN: 4.02, CZK: 23.15, HUF: 358.5, SEK: 10.45, NOK: 10.62, DKK: 6.87
      });
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchExchangeRates();
    
    // Refresh rates every 30 minutes
    const rateInterval = setInterval(fetchExchangeRates, 30 * 60 * 1000);
    return () => clearInterval(rateInterval);
  }, [fetchExchangeRates]);

  // Convert amount from USD to selected currency
  const convertCurrency = useCallback((amountInUSD, toCurrency = selectedCurrency) => {
    if (!exchangeRates[toCurrency]) return amountInUSD;
    return amountInUSD * exchangeRates[toCurrency];
  }, [exchangeRates, selectedCurrency]);

  // Format currency with proper symbol
  const formatCurrency = useCallback((value, currency = selectedCurrency) => {
    if (!value && value !== 0) return `${CURRENCIES[currency]?.symbol || '$'}0.00`;
    const converted = convertCurrency(value, currency);
    const currencyInfo = CURRENCIES[currency] || CURRENCIES.USD;
    
    // Format based on currency - always show full numbers
    if (currency === 'JPY' || currency === 'HUF' || currency === 'ALL') {
      return `${currencyInfo.symbol}${Math.round(converted).toLocaleString()}`;
    }
    
    return `${currencyInfo.symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [convertCurrency, selectedCurrency]);

  const formatFullCurrency = useCallback((value, currency = selectedCurrency) => {
    if (!value && value !== 0) return `${CURRENCIES[currency]?.symbol || '$'}0.00`;
    const converted = convertCurrency(Math.abs(value), currency);
    const currencyInfo = CURRENCIES[currency] || CURRENCIES.USD;
    const prefix = value < 0 ? '-' : '';
    
    if (currency === 'JPY' || currency === 'HUF' || currency === 'ALL') {
      return `${prefix}${currencyInfo.symbol}${Math.round(converted).toLocaleString()}`;
    }
    
    return `${prefix}${currencyInfo.symbol}${converted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [convertCurrency, selectedCurrency]);

  // Handle currency change
  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
    toast.success(`Monedha u ndryshua në ${CURRENCIES[currency]?.name}`);
  };

  // Calculate exchange
  const calculateExchange = useCallback(() => {
    if (!exchangeRates[exchangeFrom] || !exchangeRates[exchangeTo]) return;
    
    const amountNum = parseFloat(exchangeAmount) || 0;
    // Convert from source currency to USD first, then to target
    const inUSD = amountNum / exchangeRates[exchangeFrom];
    const result = inUSD * exchangeRates[exchangeTo];
    setConvertedAmount(result);
  }, [exchangeAmount, exchangeFrom, exchangeTo, exchangeRates]);

  useEffect(() => {
    calculateExchange();
  }, [calculateExchange]);

  // Swap currencies
  const swapCurrencies = () => {
    const temp = exchangeFrom;
    setExchangeFrom(exchangeTo);
    setExchangeTo(temp);
  };

  const fetchOverview = async () => {
    try {
      const response = await dashboardAPI.getOverview();
      setOverview(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch general expenses from database
  const fetchExpenses = async () => {
    try {
      const response = await expensesAPI.getAll();
      if (response.data && response.data.length > 0) {
        // Transform to match local state format
        const dbExpenses = response.data.map(exp => ({
          id: exp.expense_id,
          expense_id: exp.expense_id,
          description: exp.description,
          category: exp.category,
          amount: exp.amount,
          date: exp.date,
          status: exp.status || 'approved'
        }));
        setExpenses(dbExpenses);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  // Fetch workers
  const fetchWorkers = async () => {
    try {
      const [workersRes, statsRes] = await Promise.all([
        workersAPI.getAll(),
        workersAPI.getStats()
      ]);
      setWorkers(workersRes.data);
      setWorkersStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  };

  // Fetch team members
  const fetchTeamMembers = async () => {
    try {
      const [membersRes, statsRes] = await Promise.all([
        teamAPI.getAll(),
        teamAPI.getStats()
      ]);
      setTeamMembers(membersRes.data);
      setTeamStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  // Fetch clients from database
  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClientsFromDB(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Fetch client detail
  const fetchClientDetail = async (clientId) => {
    try {
      const response = await clientsAPI.get(clientId);
      setClientDetail(response.data);
    } catch (error) {
      console.error('Error fetching client detail:', error);
      toast.error('Dështoi ngarkimi i të dhënave të klientit');
    }
  };

  // Fetch book-keeping data
  const fetchBookkeepingData = async () => {
    try {
      const [statsRes, ledgerRes, journalRes, accountsRes, docsRes, analysesRes] = await Promise.all([
        bookkeepingAPI.getStats(),
        bookkeepingAPI.getLedger(),
        bookkeepingAPI.getJournal(),
        bookkeepingAPI.getAccounts(),
        bookkeepingAPI.getDocuments(),
        bookkeepingAPI.getAnalyses().catch(() => ({ data: [] }))
      ]);
      setBookkeepingStats(statsRes.data);
      setLedgerEntries(ledgerRes.data);
      setJournalEntries(journalRes.data);
      setChartOfAccounts(accountsRes.data);
      setBookkeepingDocuments(docsRes.data);
      setBookkeepingAnalyses(analysesRes.data || []);
    } catch (error) {
      console.error('Error fetching bookkeeping data:', error);
    }
  };

  const fetchTrialBalance = async () => {
    try {
      const response = await bookkeepingAPI.getTrialBalance();
      setTrialBalance(response.data);
    } catch (error) {
      console.error('Error fetching trial balance:', error);
    }
  };

  // Load workers and team on mount
  useEffect(() => {
    fetchWorkers();
    fetchTeamMembers();
    fetchClients();
    fetchExpenses();
    fetchBookkeepingData();
  }, []);

  // Apply dark mode class to document root
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('financial_dark_mode', String(darkMode));
  }, [darkMode]);

  const openSettings = async () => {
    setSettingsName(user?.name || '');
    setPwForm({ current: '', next: '', confirm: '' });
    setTwoFACode('');
    setShowTwoFAVerify(false);
    setShowSettingsModal(true);
    
    // Fetch 2FA status
    try {
      const [twoFARes, adminRes] = await Promise.all([
        authAPI.get2FAStatus(),
        adminAPI.checkAdmin().catch(() => ({ data: { is_admin: false } }))
      ]);
      setTwoFAEnabled(twoFARes.data.enabled);
      setIsAdmin(adminRes.data.is_admin);
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error);
    }
  };

  const handleSaveProfile = async () => {
    if (!settingsName.trim()) {
      toast.error('Emri nuk mund të jetë bosh');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await authAPI.updateProfile({ name: settingsName.trim() });
      if (updateUserData) updateUserData(res.data);
      toast.success('Profili u përditësua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi përditësimi i profilit');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.next) {
      toast.error('Plotësoni fjalëkalimin aktual dhe të riun');
      return;
    }
    if (pwForm.next.length < 6) {
      toast.error('Fjalëkalimi i ri duhet të jetë së paku 6 karaktere');
      return;
    }
    if (pwForm.next !== pwForm.confirm) {
      toast.error('Fjalëkalimet e reja nuk përputhen');
      return;
    }
    setChangingPw(true);
    try {
      await authAPI.changePassword({ current_password: pwForm.current, new_password: pwForm.next });
      setPwForm({ current: '', next: '', confirm: '' });
      toast.success('Fjalëkalimi u ndryshua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi ndryshimi i fjalëkalimit');
    } finally {
      setChangingPw(false);
    }
  };

  // 2FA handlers
  const handleToggle2FA = async () => {
    setTwoFALoading(true);
    try {
      if (twoFAEnabled) {
        // Start disable flow - request code
        const response = await authAPI.disable2FA('', null);
        setTwoFASetupId(response.data.setup_id);
        setShowTwoFAVerify(true);
        toast.info(response.data.message || 'Kodi u dërgua në email');
      } else {
        // Start enable flow
        const response = await authAPI.enable2FA();
        setTwoFASetupId(response.data.setup_id);
        setShowTwoFAVerify(true);
        toast.info(response.data.message || 'Kodi u dërgua në email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi operacioni');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FAToggle = async () => {
    if (!twoFACode.trim() || twoFACode.length !== 6) {
      toast.error('Vendosni kodin 6-shifror');
      return;
    }
    
    setTwoFALoading(true);
    try {
      if (twoFAEnabled) {
        // Confirm disable
        await authAPI.disable2FA(twoFACode, twoFASetupId);
        setTwoFAEnabled(false);
        toast.success('2FA u çaktivizua me sukses');
      } else {
        // Confirm enable
        await authAPI.confirmEnable2FA(twoFACode, twoFASetupId);
        setTwoFAEnabled(true);
        toast.success('2FA u aktivizua me sukses');
      }
      setShowTwoFAVerify(false);
      setTwoFACode('');
      setTwoFASetupId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kodi është i pasaktë');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleCompanyAdded = () => {
    fetchOverview();
    setShowAddCompany(false);
    toast.success('Kompania u shtua me sukses!');
  };

  // Add handlers
  const handleAddInvoice = () => {
    if (!newInvoice.client || !newInvoice.amount) {
      toast.error('Ju lutem plotësoni të gjitha fushat');
      return;
    }
    const invoice = {
      id: `INV-${String(invoices.length + 1).padStart(3, '0')}`,
      client: newInvoice.client,
      amount: parseFloat(newInvoice.amount),
      date: new Date().toISOString().split('T')[0],
      dueDate: newInvoice.dueDate || new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      status: 'pending'
    };
    setInvoices([invoice, ...invoices]);
    setNewInvoice({ client: '', amount: '', dueDate: '', description: '' });
    setShowInvoiceModal(false);
    toast.success('Fatura u krijua me sukses!');
  };

  const handleAddExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      toast.error('Ju lutem plotësoni të gjitha fushat');
      return;
    }
    try {
      await expensesAPI.create({
        description: newExpense.description,
        category: newExpense.category,
        amount: parseFloat(newExpense.amount),
        date: newExpense.date || null
      });
      setNewExpense({ description: '', category: 'supplies', amount: '', date: '' });
      setShowExpenseModal(false);
      fetchExpenses(); // Refresh from database
      toast.success('Shpenzimi u regjistrua!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi regjistrimi i shpenzimit');
    }
  };

  const handleAddTransaction = () => {
    if (!newTransaction.description || !newTransaction.amount) {
      toast.error('Ju lutem plotësoni të gjitha fushat');
      return;
    }
    const transaction = {
      id: `TRX-${String(transactions.length + 1).padStart(3, '0')}`,
      description: newTransaction.description,
      type: newTransaction.type,
      amount: newTransaction.type === 'expense' ? -Math.abs(parseFloat(newTransaction.amount)) : Math.abs(parseFloat(newTransaction.amount)),
      date: new Date().toISOString().split('T')[0],
      account: newTransaction.account
    };
    setTransactions([transaction, ...transactions]);
    setNewTransaction({ description: '', type: 'income', amount: '', account: 'Bank Account' });
    setShowTransactionModal(false);
    toast.success('Transaksioni u regjistrua!');
  };

  // Worker handlers
  const handleAddWorker = async () => {
    if (!newWorker.name || !newWorker.role || !newWorker.monthly_wage) {
      toast.error('Ju lutem plotësoni emrin, rolin dhe pagën');
      return;
    }
    try {
      await workersAPI.create({
        name: newWorker.name,
        role: newWorker.role,
        monthly_wage: parseFloat(newWorker.monthly_wage),
        bonus: parseFloat(newWorker.bonus) || 0,
        hire_date: newWorker.hire_date || null,
        notes: newWorker.notes || null,
        client_id: newWorker.client_id || null
      });
      setNewWorker({ name: '', role: '', monthly_wage: '', bonus: 0, hire_date: '', notes: '', client_id: null });
      setShowWorkerModal(false);
      fetchWorkers();
      toast.success('Punëtori u shtua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi shtimi i punëtorit');
    }
  };

  const handleUpdateWorker = async () => {
    if (!editingWorker) return;
    try {
      await workersAPI.update(editingWorker.worker_id, {
        name: editingWorker.name,
        role: editingWorker.role,
        monthly_wage: parseFloat(editingWorker.monthly_wage),
        bonus: parseFloat(editingWorker.bonus) || 0,
        hire_date: editingWorker.hire_date,
        notes: editingWorker.notes,
        client_id: editingWorker.client_id || null
      });
      setEditingWorker(null);
      fetchWorkers();
      toast.success('Punëtori u përditësua!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi përditësimi');
    }
  };

  const handleDeleteWorker = async (workerId) => {
    if (!window.confirm('Jeni të sigurt që doni të fshini këtë punëtor?')) return;
    try {
      await workersAPI.delete(workerId);
      fetchWorkers();
      toast.success('Punëtori u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja e punëtorit');
    }
  };

  // Team handlers
  const handleInviteTeamMember = async () => {
    if (!newTeamMember.email) {
      toast.error('Ju lutem vendosni emailin');
      return;
    }
    try {
      await teamAPI.invite({
        email: newTeamMember.email,
        role: newTeamMember.role
      });
      setNewTeamMember({ email: '', role: 'viewer' });
      setShowTeamModal(false);
      fetchTeamMembers();
      toast.success('Ftesa u dërgua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi dërgimi i ftesës');
    }
  };

  const handleUpdateTeamMember = async (memberId, role) => {
    try {
      await teamAPI.update(memberId, { role });
      fetchTeamMembers();
      toast.success('Roli u përditësua!');
    } catch (error) {
      toast.error('Dështoi përditësimi i rolit');
    }
  };

  const handleRemoveTeamMember = async (memberId) => {
    if (!window.confirm('Jeni të sigurt që doni të hiqni këtë anëtar?')) return;
    try {
      await teamAPI.remove(memberId);
      fetchTeamMembers();
      toast.success('Anëtari u hoq!');
    } catch (error) {
      toast.error('Dështoi heqja e anëtarit');
    }
  };

  // Client handlers
  const handleCreateClient = async () => {
    if (!newClientData.name) {
      toast.error('Ju lutem vendosni emrin e klientit');
      return;
    }
    try {
      await clientsAPI.create(newClientData);
      setNewClientData({ name: '', email: '', phone: '', address: '', notes: '' });
      setShowClientModal(false);
      fetchClients();
      toast.success('Klienti u shtua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi shtimi i klientit');
    }
  };

  const handleOpenClientDetail = async (clientId) => {
    setSelectedClient(clientId);
    await fetchClientDetail(clientId);
  };

  const handleClientCardClick = async (client) => {
    // Real DB client -> open detail directly
    if (client.client_id) {
      handleOpenClientDetail(client.client_id);
      return;
    }
    // Demo/placeholder client -> promote demo clients to real DB records so details work
    try {
      let createdId = null;
      if (clientsFromDB.length === 0) {
        for (const demo of clients) {
          const res = await clientsAPI.create({ name: demo.name, email: demo.email || '', phone: demo.phone || '' });
          if (demo.id === client.id) createdId = res.data.client_id;
        }
      } else {
        const res = await clientsAPI.create({ name: client.name, email: client.email || '', phone: client.phone || '' });
        createdId = res.data.client_id;
      }
      await fetchClients();
      if (createdId) handleOpenClientDetail(createdId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Dështoi hapja e detajeve të klientit');
    }
  };

  const handleCloseClientDetail = () => {
    setSelectedClient(null);
    setClientDetail(null);
  };

  const handleCreateClientInvoice = async () => {
    if (!newClientInvoice.amount || !selectedClient) {
      toast.error('Ju lutem vendosni shumën');
      return;
    }
    try {
      await clientsAPI.createInvoice(selectedClient, {
        amount: parseFloat(newClientInvoice.amount),
        description: newClientInvoice.description,
        due_date: newClientInvoice.due_date || null,
        status: newClientInvoice.status
      });
      setNewClientInvoice({ amount: '', description: '', due_date: '', status: 'pending' });
      setShowClientInvoiceModal(false);
      fetchClientDetail(selectedClient);
      toast.success('Fatura u krijua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi krijimi i faturës');
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, status) => {
    try {
      await clientsAPI.updateInvoice(selectedClient, invoiceId, { status });
      fetchClientDetail(selectedClient);
      toast.success('Statusi u përditësua!');
    } catch (error) {
      toast.error('Dështoi përditësimi');
    }
  };

  const handleDeleteClientInvoice = async (invoiceId) => {
    if (!window.confirm('Jeni të sigurt që doni të fshini këtë faturë?')) return;
    try {
      await clientsAPI.deleteInvoice(selectedClient, invoiceId);
      fetchClientDetail(selectedClient);
      toast.success('Fatura u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja');
    }
  };

  const handleCreateClientExpense = async () => {
    if (!newClientExpense.description || !newClientExpense.amount || !selectedClient) {
      toast.error('Ju lutem plotësoni të gjitha fushat');
      return;
    }
    try {
      await clientsAPI.createExpense(selectedClient, {
        description: newClientExpense.description,
        category: newClientExpense.category,
        amount: parseFloat(newClientExpense.amount),
        date: newClientExpense.date || null
      });
      setNewClientExpense({ description: '', category: 'supplies', amount: '', date: '' });
      setShowClientExpenseModal(false);
      fetchClientDetail(selectedClient);
      toast.success('Shpenzimi u regjistrua!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi regjistrimi');
    }
  };

  const handleDeleteClientExpense = async (expenseId) => {
    if (!window.confirm('Jeni të sigurt që doni të fshini këtë shpenzim?')) return;
    try {
      await clientsAPI.deleteExpense(selectedClient, expenseId);
      fetchClientDetail(selectedClient);
      toast.success('Shpenzimi u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Jeni të sigurt? Kjo do të fshijë të gjitha të dhënat e klientit.')) return;
    try {
      await clientsAPI.delete(clientId);
      handleCloseClientDetail();
      fetchClients();
      toast.success('Klienti u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja e klientit');
    }
  };

  // Book-keeping handlers
  const handleCreateLedgerEntry = async () => {
    if (!newLedgerEntry.account_name || (!newLedgerEntry.debit && !newLedgerEntry.credit)) {
      toast.error('Ju lutem plotësoni llogarinë dhe shumën');
      return;
    }
    try {
      await bookkeepingAPI.createLedgerEntry({
        account_name: newLedgerEntry.account_name,
        account_type: newLedgerEntry.account_type,
        debit: parseFloat(newLedgerEntry.debit) || 0,
        credit: parseFloat(newLedgerEntry.credit) || 0,
        description: newLedgerEntry.description,
        reference: newLedgerEntry.reference,
        date: newLedgerEntry.date || null
      });
      setNewLedgerEntry({ account_name: '', account_type: 'expense', debit: '', credit: '', description: '', reference: '', date: '' });
      setShowLedgerModal(false);
      fetchBookkeepingData();
      toast.success('Regjistrimi u shtua!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi regjistrimi');
    }
  };

  const handleCreateJournalEntry = async () => {
    if (!newJournalEntry.description || newJournalEntry.entries.length < 2) {
      toast.error('Ju lutem plotësoni përshkrimin dhe së paku 2 regjistrime');
      return;
    }
    // Validate debits = credits
    const totalDebit = newJournalEntry.entries.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0);
    const totalCredit = newJournalEntry.entries.reduce((s, e) => s + (parseFloat(e.credit) || 0), 0);
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error('Debiti dhe krediti duhet të jenë të barabartë!');
      return;
    }
    try {
      await bookkeepingAPI.createJournalEntry({
        description: newJournalEntry.description,
        date: newJournalEntry.date || null,
        reference: newJournalEntry.reference,
        entries: newJournalEntry.entries.map(e => ({
          account: e.account,
          type: e.type,
          debit: parseFloat(e.debit) || 0,
          credit: parseFloat(e.credit) || 0
        }))
      });
      setNewJournalEntry({ description: '', date: '', reference: '', entries: [{ account: '', type: 'expense', debit: '', credit: '' }, { account: '', type: 'expense', debit: '', credit: '' }] });
      setShowJournalModal(false);
      fetchBookkeepingData();
      toast.success('Ditari u krijua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi krijimi i ditarit');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Skedari është shumë i madh. Maksimumi është 10MB.');
        return;
      }
      setNewDocument({ ...newDocument, file });
    }
  };

  const handleCreateDocument = async () => {
    if (!newDocument.name) {
      toast.error('Ju lutem vendosni emrin e dokumentit');
      return;
    }
    
    setUploadingDocument(true);
    try {
      let documentData = {
        name: newDocument.name,
        document_type: newDocument.document_type,
        notes: newDocument.notes
      };

      // If file is selected, convert to base64
      if (newDocument.file) {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const base64String = reader.result.split(',')[1];
            resolve(base64String);
          };
          reader.onerror = reject;
          reader.readAsDataURL(newDocument.file);
        });

        documentData.file_data = base64;
        documentData.file_type = newDocument.file.type;
        documentData.file_name = newDocument.file.name;
      }

      await bookkeepingAPI.createDocument(documentData);
      setNewDocument({ name: '', document_type: 'invoice', notes: '', file: null });
      setShowDocumentModal(false);
      fetchBookkeepingData();
      toast.success('Dokumenti u ngarkua me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi ngarkimi i dokumentit');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleAnalyzeBookkeeping = async () => {
    if (bookkeepingDocuments.length === 0) {
      toast.error('Ju lutem ngarkoni disa dokumente së pari');
      return;
    }
    
    setAnalyzingBookkeeping(true);
    try {
      const response = await bookkeepingAPI.analyzeDocuments('comprehensive');
      setCurrentAnalysis(response.data);
      setShowAnalysisModal(true);
      // Refresh analyses list
      const analysesRes = await bookkeepingAPI.getAnalyses();
      setBookkeepingAnalyses(analysesRes.data || []);
      toast.success('Analiza u krye me sukses!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi analizimi i dokumenteve');
    } finally {
      setAnalyzingBookkeeping(false);
    }
  };

  const fetchBookkeepingAnalyses = async () => {
    try {
      const response = await bookkeepingAPI.getAnalyses();
      setBookkeepingAnalyses(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bookkeeping analyses:', error);
    }
  };

  // ===== CLOUD DOCUMENTS: preview / download / export =====
  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handlePreviewDocument = async (doc) => {
    if (!doc.file_path && !doc.file_name) {
      toast.error('Ky dokument nuk ka skedar të bashkëngjitur');
      return;
    }
    setPreviewLoading(true);
    setShowPreviewModal(true);
    setPreviewDoc({ name: doc.name, file_type: doc.file_type, file_name: doc.file_name, dataUrl: null });
    try {
      const res = await bookkeepingAPI.getDocumentFile(doc.document_id);
      const { file_data, file_type, file_name } = res.data;
      const dataUrl = `data:${file_type || 'application/octet-stream'};base64,${file_data}`;
      setPreviewDoc({ name: doc.name, file_type, file_name, dataUrl });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi hapja e dokumentit');
      setShowPreviewModal(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const res = await bookkeepingAPI.getDocumentFile(doc.document_id);
      const { file_data, file_type, file_name } = res.data;
      const byteChars = atob(file_data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNumbers[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: file_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file_name || `${doc.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dështoi shkarkimi i dokumentit');
    }
  };

  // ===== REPORT EXPORTS (PDF / Excel / CSV) =====
  const getReportData = (reportType) => {
    const fd = financialData;
    const cur = selectedCurrency;
    const map = {
      income: {
        title: 'Pasqyra e të Ardhurave',
        rows: [
          ['Të ardhura nga shërbimet', fd.incomeStatement.revenue],
          ['Kosto e mallrave të shitura', -fd.incomeStatement.costOfGoodsSold],
          ['Fitimi Bruto', fd.incomeStatement.grossProfit],
          ['Shpenzime operative', -fd.incomeStatement.operatingExpenses],
          ['Fitimi Operativ', fd.incomeStatement.operatingIncome],
          ['Interesa', -fd.incomeStatement.interestExpense],
          ['Tatime', -fd.incomeStatement.taxExpense],
          ['Fitimi Neto', fd.incomeStatement.netIncome],
        ],
      },
      balance: {
        title: 'Bilanci Kontabël',
        rows: [
          ['Para në dorë', fd.balanceSheet.cash],
          ['Llogari të arkëtueshme', fd.balanceSheet.accountsReceivable],
          ['Inventar', fd.balanceSheet.inventory],
          ['Total Asete Rrjedhëse', fd.balanceSheet.totalCurrentAssets],
          ['Pajisje & mobilje', fd.balanceSheet.propertyEquipment],
          ['TOTAL ASETE', fd.balanceSheet.totalAssets],
          ['Llogari të pagueshme', fd.balanceSheet.accountsPayable],
          ['Borxh afatshkurtër', fd.balanceSheet.shortTermDebt],
          ['Borxh afatgjatë', fd.balanceSheet.longTermDebt],
          ['Total Detyrime', fd.balanceSheet.totalLiabilities],
          ['Kapitali i aksionerëve', fd.balanceSheet.shareholdersEquity],
          ['Fitime të mbajtura', fd.balanceSheet.retainedEarnings],
          ['Total Kapital', fd.balanceSheet.totalEquity],
        ],
      },
      cashflow: {
        title: 'Pasqyra e Rrjedhës së Parasë',
        rows: [
          ['Rrjedha operative', fd.cashFlow.operatingCashFlow],
          ['Rrjedha investuese', fd.cashFlow.investingCashFlow],
          ['Rrjedha financuese', fd.cashFlow.financingCashFlow],
          ['Ndryshimi neto në para', fd.cashFlow.netChangeInCash],
          ['Para në fillim', fd.cashFlow.beginningCash],
          ['Para në fund', fd.cashFlow.endingCash],
        ],
      },
      expenses: {
        title: 'Raport Shpenzimesh',
        rows: expenses.map((e) => [`${e.description} (${e.category})`, e.amount]),
      },
      clients: {
        title: 'Raport Klientësh',
        rows: clients.map((c) => [c.name || c.company_name || 'Klient', c.total_billed || c.balance || 0]),
      },
      tax: {
        title: 'Raport Tatimor',
        rows: [
          ['Të ardhura të tatueshme', fd.incomeStatement.revenue],
          ['Shpenzime të zbritshme', fd.incomeStatement.operatingExpenses + fd.incomeStatement.costOfGoodsSold],
          ['Fitimi para tatimit', fd.incomeStatement.operatingIncome - fd.incomeStatement.interestExpense],
          ['Tatim mbi fitimin (15%)', fd.incomeStatement.taxExpense],
          ['TVSH (18%)', fd.incomeStatement.revenue * 0.18],
        ],
      },
    };
    return map[reportType] || map.income;
  };

  const handleExportReport = (reportType, format) => {
    const data = getReportData(reportType);
    const fmtMoney = (v) => formatFullCurrency(v);
    const filenameBase = data.title.replace(/\s+/g, '_');
    try {
      if (format === 'pdf') {
        exportReportPDF({
          title: data.title,
          subtitle: `Valuta: ${selectedCurrency} • ${new Date().toLocaleDateString('sq-AL')}`,
          sections: [{ heading: data.title, rows: data.rows.map(([l, v]) => [l, fmtMoney(v)]) }],
          filename: `${filenameBase}.pdf`,
        });
      } else if (format === 'excel') {
        exportExcel({
          sheets: [{ name: data.title.slice(0, 28), aoa: [['Përshkrimi', `Vlera (${selectedCurrency})`], ...data.rows] }],
          filename: `${filenameBase}.xlsx`,
        });
      } else {
        exportCSV({
          rows: [['Përshkrimi', `Vlera (${selectedCurrency})`], ...data.rows],
          filename: `${filenameBase}.csv`,
        });
      }
      toast.success(`Raporti u eksportua (${format.toUpperCase()})`);
    } catch (e) {
      toast.error('Dështoi eksportimi i raportit');
    }
  };

  const handleExportDocumentsList = (format) => {
    const typeLabels = {
      invoice: 'Faturë', receipt: 'Faturë Blerje', bank_statement: 'Pasqyrë Bankare',
      contract: 'Kontratë', tax_document: 'Dokument Tatimor', other: 'Tjetër',
    };
    const header = ['Emri', 'Lloji', 'Skedari', 'Madhësia', 'Data'];
    const rows = bookkeepingDocuments.map((d) => [
      d.name,
      typeLabels[d.document_type] || d.document_type,
      d.file_name || '—',
      formatFileSize(d.file_size),
      (d.created_at || '').split('T')[0],
    ]);
    try {
      if (format === 'excel') {
        exportExcel({ sheets: [{ name: 'Dokumentet', aoa: [header, ...rows] }], filename: 'Dokumentet_Cloud.xlsx' });
      } else if (format === 'csv') {
        exportCSV({ rows: [header, ...rows], filename: 'Dokumentet_Cloud.csv' });
      } else {
        exportReportPDF({
          title: 'Lista e Dokumenteve në Cloud',
          subtitle: `${bookkeepingDocuments.length} dokumente • ${new Date().toLocaleDateString('sq-AL')}`,
          sections: [{ heading: 'Dokumentet', rows: rows.map((r) => [`${r[0]} — ${r[1]}`, `${r[3]} • ${r[4]}`]) }],
          filename: 'Dokumentet_Cloud.pdf',
        });
      }
      toast.success(`Lista u eksportua (${format.toUpperCase()})`);
    } catch (e) {
      toast.error('Dështoi eksportimi');
    }
  };

  const handleExportAnalysisPDF = (analysis) => {
    if (!analysis?.insights) {
      toast.error('Nuk ka analizë për të eksportuar');
      return;
    }
    try {
      exportAnalysisPDF({
        title: 'Analiza AI e Dokumenteve',
        dateStr: new Date(analysis.created_at || Date.now()).toLocaleDateString('sq-AL'),
        text: analysis.insights,
        filename: 'Analiza_AI_FinanciAL.pdf',
      });
      toast.success('Analiza u eksportua në PDF');
    } catch (e) {
      toast.error('Dështoi eksportimi i analizës');
    }
  };

  const renderReportExportMenu = (reportType) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`export-report-${reportType}`}>
          <Export className="w-4 h-4 mr-2" />
          Eksporto
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => handleExportReport(reportType, 'pdf')}>PDF</DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportReport(reportType, 'excel')}>Excel</DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExportReport(reportType, 'csv')}>CSV</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );


  const handleDeleteLedgerEntry = async (entryId) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await bookkeepingAPI.deleteLedgerEntry(entryId);
      fetchBookkeepingData();
      toast.success('Regjistrimi u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja');
    }
  };

  const handleDeleteJournalEntry = async (journalId) => {
    if (!window.confirm('Jeni të sigurt? Kjo do të fshijë edhe regjistrimet përkatëse.')) return;
    try {
      await bookkeepingAPI.deleteJournalEntry(journalId);
      fetchBookkeepingData();
      toast.success('Ditari u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Jeni të sigurt?')) return;
    try {
      await bookkeepingAPI.deleteDocument(documentId);
      fetchBookkeepingData();
      toast.success('Dokumenti u fshi!');
    } catch (error) {
      toast.error('Dështoi fshirja');
    }
  };

  const addJournalLine = () => {
    setNewJournalEntry({
      ...newJournalEntry,
      entries: [...newJournalEntry.entries, { account: '', type: 'expense', debit: '', credit: '' }]
    });
  };

  const updateJournalLine = (index, field, value) => {
    const updated = [...newJournalEntry.entries];
    updated[index][field] = value;
    setNewJournalEntry({ ...newJournalEntry, entries: updated });
  };

  const removeJournalLine = (index) => {
    if (newJournalEntry.entries.length <= 2) return;
    const updated = newJournalEntry.entries.filter((_, i) => i !== index);
    setNewJournalEntry({ ...newJournalEntry, entries: updated });
  };

  // Calculate totals
  const totalInvoiced = invoices.reduce((sum, inv) => sum + inv.amount, 0);
  const totalPending = invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + inv.amount, 0);
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalIncome = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);

  // Calculate expenses by category dynamically
  const expensesByCategory = React.useMemo(() => {
    const categoryMap = {
      supplies: { name: 'Furnizime', color: '#1A3C34' },
      travel: { name: 'Udhëtime', color: '#2A9D8F' },
      meals: { name: 'Ushqim', color: '#D4A373' },
      software: { name: 'Software', color: '#E9C46A' },
      rent: { name: 'Qira', color: '#264653' },
      utilities: { name: 'Shërbime', color: '#E76F51' },
      marketing: { name: 'Marketing', color: '#F4A261' },
      insurance: { name: 'Sigurime', color: '#287271' }
    };
    
    const grouped = {};
    expenses.forEach(exp => {
      const cat = exp.category || 'other';
      if (!grouped[cat]) {
        grouped[cat] = { 
          name: categoryMap[cat]?.name || cat, 
          value: 0, 
          color: categoryMap[cat]?.color || '#888888' 
        };
      }
      grouped[cat].value += exp.amount;
    });
    
    return Object.values(grouped).sort((a, b) => b.value - a.value);
  }, [expenses]);

  // Calculate monthly revenue data dynamically
  const revenueChartData = React.useMemo(() => {
    // Get last 6 months based on real data - show zeros for empty months
    const monthNames = ['Jan', 'Shk', 'Mar', 'Pri', 'Maj', 'Qer', 'Kor', 'Gush', 'Shta', 'Tet', 'Nën', 'Dhj'];
    const now = new Date();
    const months = [];
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = monthNames[date.getMonth()];
      
      // Calculate income from invoices for this month
      const monthIncome = invoices
        .filter(inv => inv.status === 'paid' && inv.date?.startsWith(monthKey))
        .reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0);
      
      // Calculate expenses for this month  
      const monthExpenses = expenses
        .filter(exp => exp.date?.startsWith(monthKey))
        .reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
      
      months.push({
        month: monthName,
        income: monthIncome,
        expenses: monthExpenses
      });
    }
    
    return months;
  }, [invoices, expenses]);

  // Financial statements data for reports - use only real data
  const financialData = React.useMemo(() => ({
    incomeStatement: {
      revenue: totalIncome,
      costOfGoodsSold: totalExpenses * 0.4,
      grossProfit: totalIncome - (totalExpenses * 0.4),
      operatingExpenses: totalExpenses * 0.6,
      operatingIncome: totalIncome - totalExpenses,
      interestExpense: 0,
      taxExpense: Math.max(0, (totalIncome - totalExpenses) * 0.15),
      netIncome: Math.max(0, (totalIncome - totalExpenses) * 0.85)
    },
    balanceSheet: {
      // Assets - based on actual data
      cash: Math.max(0, totalIncome - totalExpenses),
      accountsReceivable: totalPending,
      inventory: 0,
      totalCurrentAssets: Math.max(0, totalIncome - totalExpenses) + totalPending,
      propertyEquipment: 0,
      totalAssets: Math.max(0, totalIncome - totalExpenses) + totalPending,
      // Liabilities
      accountsPayable: 0,
      shortTermDebt: 0,
      totalCurrentLiabilities: 0,
      longTermDebt: 0,
      totalLiabilities: 0,
      // Equity
      shareholdersEquity: 0,
      retainedEarnings: Math.max(0, totalIncome - totalExpenses) + totalPending,
      totalEquity: Math.max(0, totalIncome - totalExpenses) + totalPending
    },
    cashFlow: {
      operatingCashFlow: totalIncome - totalExpenses,
      investingCashFlow: 0,
      financingCashFlow: 0,
      netChangeInCash: totalIncome - totalExpenses,
      beginningCash: 0,
      endingCash: totalIncome - totalExpenses
    }
  }), [totalIncome, totalExpenses, totalPending]);

  const navItems = [
    { id: 'dashboard', icon: House, label: 'Paneli Kryesor' },
    { id: 'companies', icon: Buildings, label: 'Kompanitë', badge: overview?.total_companies },
    { id: 'bookkeeping', icon: BookOpen, label: 'Kontabiliteti', badge: bookkeepingStats?.journal_entries || null },
    { id: 'documents', icon: CloudArrowUp, label: 'Dokumentet', badge: bookkeepingStats?.documents || null },
    { id: 'workers', icon: Briefcase, label: 'Punëtorët', badge: workersStats?.total_workers || null },
    { id: 'team', icon: UsersThree, label: 'Ekipi', badge: teamStats?.total_members || null },
    { id: 'invoices', icon: Invoice, label: 'Faturat', badge: invoices.filter(i => i.status === 'pending').length || null },
    { id: 'expenses', icon: Wallet, label: 'Shpenzimet' },
    { id: 'transactions', icon: ArrowsLeftRight, label: 'Transaksionet' },
    { id: 'clients', icon: Users, label: 'Klientët' },
    { id: 'exchanger', icon: CurrencyCircleDollar, label: 'Këmbimi Valutor' },
    { id: 'reports', icon: ChartBar, label: 'Raportet' },
    { id: 'insights', icon: Sparkle, label: 'Njohuri AI' },
  ];

  // ===== RENDER CONTENT BASED ON ACTIVE NAV =====
  const renderContent = () => {
    switch (activeNav) {
      case 'dashboard': return renderDashboard();
      case 'companies': return renderCompanies();
      case 'bookkeeping': return renderBookkeeping();
      case 'documents': return renderDocuments();
      case 'workers': return renderWorkers();
      case 'team': return renderTeam();
      case 'invoices': return renderInvoices();
      case 'expenses': return renderExpenses();
      case 'transactions': return renderTransactions();
      case 'clients': return renderClients();
      case 'exchanger': return renderExchanger();
      case 'reports': return renderReports();
      case 'insights': return renderInsights();
      default: return renderDashboard();
    }
  };

  // ===== CURRENCY EXCHANGER VIEW =====
  const renderExchanger = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#1A3C34]">Këmbimi Valutor</h2>
        <p className="text-sm text-[#1A3C34]/60">
          Kurse këmbimi në kohë reale nga Banka Qendrore Evropiane
          {lastRateUpdate && (
            <span className="ml-2 text-xs">• Përditësuar: {lastRateUpdate.toLocaleDateString()}</span>
          )}
        </p>
      </div>

      {/* Main Converter */}
      <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* From Currency */}
          <div>
            <Label className="text-[#1A3C34]/60 text-sm mb-2 block">Nga</Label>
            <div className="flex gap-3">
              <Select value={exchangeFrom} onValueChange={setExchangeFrom}>
                <SelectTrigger className="w-32">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <span>{CURRENCIES[exchangeFrom]?.flag}</span>
                      <span>{exchangeFrom}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Object.entries(CURRENCIES).map(([code, info]) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <span>{info.flag}</span>
                        <span>{code}</span>
                        <span className="text-[#1A3C34]/50 text-xs">- {info.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                value={exchangeAmount}
                onChange={(e) => setExchangeAmount(e.target.value)}
                className="flex-1 text-xl font-mono"
                placeholder="0.00"
              />
            </div>
            <p className="text-xs text-[#1A3C34]/50 mt-2">{CURRENCIES[exchangeFrom]?.name}</p>
          </div>

          {/* Swap Button */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block">
            <button
              onClick={swapCurrencies}
              className="w-10 h-10 rounded-full bg-[#1A3C34] text-white flex items-center justify-center hover:bg-[#1A3C34]/90 transition-colors"
            >
              <ArrowsClockwise className="w-5 h-5" />
            </button>
          </div>

          {/* To Currency */}
          <div>
            <Label className="text-[#1A3C34]/60 text-sm mb-2 block">Në</Label>
            <div className="flex gap-3">
              <Select value={exchangeTo} onValueChange={setExchangeTo}>
                <SelectTrigger className="w-32">
                  <SelectValue>
                    <span className="flex items-center gap-2">
                      <span>{CURRENCIES[exchangeTo]?.flag}</span>
                      <span>{exchangeTo}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {Object.entries(CURRENCIES).map(([code, info]) => (
                    <SelectItem key={code} value={code}>
                      <span className="flex items-center gap-2">
                        <span>{info.flag}</span>
                        <span>{code}</span>
                        <span className="text-[#1A3C34]/50 text-xs">- {info.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex-1 bg-[#F8F9FA] rounded-lg px-4 py-3 flex items-center">
                <span className="text-xl font-mono font-semibold text-[#1A3C34]">
                  {ratesLoading ? '...' : (
                    convertedAmount !== null ? (
                      (exchangeTo === 'JPY' || exchangeTo === 'HUF' || exchangeTo === 'ALL') 
                        ? Math.round(convertedAmount).toLocaleString()
                        : convertedAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    ) : '0.00'
                  )}
                </span>
              </div>
            </div>
            <p className="text-xs text-[#1A3C34]/50 mt-2">{CURRENCIES[exchangeTo]?.name}</p>
          </div>
        </div>

        {/* Mobile Swap Button */}
        <div className="flex justify-center my-4 md:hidden">
          <button
            onClick={swapCurrencies}
            className="w-10 h-10 rounded-full bg-[#1A3C34] text-white flex items-center justify-center"
          >
            <ArrowsClockwise className="w-5 h-5" />
          </button>
        </div>

        {/* Exchange Rate Info */}
        <div className="mt-6 pt-6 border-t border-[#1A3C34]/5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[#1A3C34]/60">Kursi i Këmbimit</p>
              <p className="text-lg font-mono font-semibold text-[#1A3C34]">
                1 {exchangeFrom} = {
                  exchangeRates[exchangeFrom] && exchangeRates[exchangeTo] 
                    ? (exchangeRates[exchangeTo] / exchangeRates[exchangeFrom]).toFixed(4)
                    : '...'
                } {exchangeTo}
              </p>
            </div>
            <Button variant="outline" onClick={fetchExchangeRates} disabled={ratesLoading}>
              <ArrowsClockwise className={`w-4 h-4 mr-2 ${ratesLoading ? 'animate-spin' : ''}`} />
              Përditëso
            </Button>
          </div>
        </div>
      </div>

      {/* Popular Currency Rates */}
      <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-6">
        <h3 className="font-semibold text-[#1A3C34] mb-4">Kurset Popullore (bazuar në USD)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {['EUR', 'GBP', 'ALL', 'CHF', 'JPY', 'CAD', 'AUD', 'TRY', 'PLN', 'RON', 'BGN', 'RSD'].map(code => (
            <div key={code} className="p-3 rounded-lg bg-[#F8F9FA] hover:bg-[#1A3C34]/5 transition-colors">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">{CURRENCIES[code]?.flag}</span>
                <span className="font-medium text-[#1A3C34]">{code}</span>
              </div>
              <p className="font-mono text-sm text-[#1A3C34]/70">
                {exchangeRates[code] ? exchangeRates[code].toFixed(code === 'JPY' || code === 'HUF' || code === 'ALL' ? 2 : 4) : '...'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Convert Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { from: 'USD', to: 'EUR', amount: 1000 },
          { from: 'EUR', to: 'ALL', amount: 100 },
          { from: 'GBP', to: 'USD', amount: 500 },
        ].map((conv, i) => {
          const inUSD = conv.amount / (exchangeRates[conv.from] || 1);
          const result = inUSD * (exchangeRates[conv.to] || 1);
          return (
            <div key={i} className="bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] rounded-xl p-5 text-white">
              <p className="text-white/70 text-sm mb-2">Konvertim i Shpejtë</p>
              <p className="text-xl font-mono font-semibold">
                {CURRENCIES[conv.from]?.symbol}{conv.amount.toLocaleString()} {conv.from}
              </p>
              <p className="text-white/50 my-1">=</p>
              <p className="text-2xl font-mono font-bold">
                {CURRENCIES[conv.to]?.symbol}{result.toLocaleString(undefined, { maximumFractionDigits: 2 })} {conv.to}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ===== DASHBOARD VIEW =====
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickStat label="Të Ardhura Totale" value={formatCurrency(totalIncome + (overview?.total_revenue || 0))} change={12.5} icon={CurrencyDollar} color="success" />
        <QuickStat label="Fatura në Pritje" value={formatCurrency(totalPending)} icon={Invoice} color="warning" />
        <QuickStat label="Shpenzime këtë Muaj" value={formatCurrency(totalExpenses)} icon={Wallet} color="danger" />
        <QuickStat label="Klientë Aktivë" value={clients.filter(c => c.status === 'active').length} icon={Users} color="default" />
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-[#1A3C34]/5 p-5">
          <h3 className="font-semibold text-[#1A3C34] mb-4">Të Ardhurat vs Shpenzimet ({selectedCurrency})</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueChartData}>
                <defs>
                  <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2A9D8F" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#2A9D8F" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#E76F51" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#E76F51" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#1A3C34', fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#1A3C34', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip formatter={(value) => [formatFullCurrency(value), '']} />
                <Area type="monotone" dataKey="income" stroke="#2A9D8F" strokeWidth={2} fill="url(#colorIncome)" name="Të Ardhura" />
                <Area type="monotone" dataKey="expenses" stroke="#E76F51" strokeWidth={2} fill="url(#colorExpenses)" name="Shpenzime" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-4">
            <h3 className="font-semibold text-[#1A3C34] mb-3">Veprime të Shpejta</h3>
            <div className="space-y-2">
              <button onClick={() => setShowInvoiceModal(true)} className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#1A3C34]/5 hover:bg-[#1A3C34]/10 transition-colors text-left">
                <div className="w-8 h-8 rounded-lg bg-[#1A3C34]/10 flex items-center justify-center">
                  <Invoice className="w-4 h-4 text-[#1A3C34]" />
                </div>
                <span className="text-sm font-medium text-[#1A3C34]">Krijo Faturë</span>
              </button>
              <button onClick={() => setShowExpenseModal(true)} className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#E76F51]/5 hover:bg-[#E76F51]/10 transition-colors text-left">
                <div className="w-8 h-8 rounded-lg bg-[#E76F51]/10 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-[#E76F51]" />
                </div>
                <span className="text-sm font-medium text-[#1A3C34]">Regjistro Shpenzim</span>
              </button>
              <button onClick={() => setActiveNav('exchanger')} className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#2A9D8F]/5 hover:bg-[#2A9D8F]/10 transition-colors text-left">
                <div className="w-8 h-8 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
                  <CurrencyCircleDollar className="w-4 h-4 text-[#2A9D8F]" />
                </div>
                <span className="text-sm font-medium text-[#1A3C34]">Këmbe Valutë</span>
              </button>
              <button onClick={() => setShowAddCompany(true)} className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#D4A373]/5 hover:bg-[#D4A373]/10 transition-colors text-left">
                <div className="w-8 h-8 rounded-lg bg-[#D4A373]/10 flex items-center justify-center">
                  <Buildings className="w-4 h-4 text-[#D4A373]" />
                </div>
                <span className="text-sm font-medium text-[#1A3C34]">Shto Kompani</span>
              </button>
            </div>
          </div>

          {/* Recent Invoices */}
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#1A3C34]">Faturat e Fundit</h3>
              <button onClick={() => setActiveNav('invoices')} className="text-xs text-[#1A3C34]/60 hover:text-[#1A3C34]">Shiko të gjitha</button>
            </div>
            <div className="space-y-2">
              {invoices.slice(0, 3).map(inv => (
                <div key={inv.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-[#F8F9FA]">
                  <div>
                    <p className="text-sm font-medium text-[#1A3C34]">{inv.client}</p>
                    <p className="text-xs text-[#1A3C34]/50">{inv.id}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono font-medium text-[#1A3C34]">{formatFullCurrency(inv.amount)}</p>
                    <StatusBadge status={inv.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[#1A3C34]/5">
          <h3 className="font-semibold text-[#1A3C34]">Transaksionet e Fundit</h3>
          <button onClick={() => setActiveNav('transactions')} className="text-sm text-[#1A3C34]/60 hover:text-[#1A3C34] flex items-center gap-1">
            Shiko të gjitha <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="divide-y divide-[#1A3C34]/5">
          {transactions.slice(0, 5).map(trx => (
            <div key={trx.id} className="flex items-center justify-between p-4 hover:bg-[#F8F9FA]">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${trx.amount >= 0 ? 'bg-[#2A9D8F]/10' : 'bg-[#E76F51]/10'}`}>
                  {trx.amount >= 0 ? <TrendUp className="w-5 h-5 text-[#2A9D8F]" /> : <TrendDown className="w-5 h-5 text-[#E76F51]" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A3C34]">{trx.description}</p>
                  <p className="text-xs text-[#1A3C34]/50">{trx.date} • {trx.account}</p>
                </div>
              </div>
              <span className={`font-mono font-medium ${trx.amount >= 0 ? 'text-[#2A9D8F]' : 'text-[#E76F51]'}`}>
                {trx.amount >= 0 ? '+' : ''}{formatFullCurrency(trx.amount)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ===== COMPANIES VIEW =====
  const renderCompanies = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3C34]">Kompanitë</h2>
          <p className="text-sm text-[#1A3C34]/60">Menaxhoni kompanitë tuaja</p>
        </div>
        <Button onClick={() => setShowAddCompany(true)} className="bg-[#1A3C34] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Shto Kompani
        </Button>
      </div>

      {overview?.company_summaries?.length > 0 ? (
        <div className="grid gap-4">
          {overview.company_summaries.map((company) => (
            <CompanyCard key={company.company_id} company={company} onClick={() => navigate(`/company/${company.company_id}`)} formatCurrency={formatCurrency} />
          ))}
        </div>
      ) : (
        <EmptyState icon={Buildings} title="Ende pa kompani" description="Shtoni kompaninë tuaj të parë për të filluar" actionLabel="Shto Kompani" onAction={() => setShowAddCompany(true)} />
      )}
    </div>
  );

  // ===== INVOICES VIEW =====
  const renderInvoices = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3C34]">Faturat</h2>
          <p className="text-sm text-[#1A3C34]/60">Krijoni dhe menaxhoni faturat</p>
        </div>
        <Button onClick={() => setShowInvoiceModal(true)} className="bg-[#1A3C34] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Krijo Faturë
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QuickStat label="Totali Faturuar" value={formatCurrency(totalInvoiced)} icon={Invoice} color="default" />
        <QuickStat label="Në Pritje" value={formatCurrency(totalPending)} icon={Clock} color="warning" />
        <QuickStat label="Të Paguara" value={formatCurrency(invoices.filter(i => i.status === 'paid').reduce((s, i) => s + i.amount, 0))} icon={Check} color="success" />
        <QuickStat label="Të Vonuara" value={formatCurrency(invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.amount, 0))} icon={Warning} color="danger" />
      </div>

      <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F8F9FA]">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Fatura</th>
                <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Klienti</th>
                <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Shuma ({selectedCurrency})</th>
                <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Data</th>
                <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Afati</th>
                <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Statusi</th>
                <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1A3C34]/5">
              {invoices.map(inv => (
                <tr key={inv.id} className="hover:bg-[#F8F9FA]">
                  <td className="p-4 font-mono text-sm text-[#1A3C34]">{inv.id}</td>
                  <td className="p-4 text-sm font-medium text-[#1A3C34]">{inv.client}</td>
                  <td className="p-4 font-mono text-sm text-[#1A3C34]">{formatFullCurrency(inv.amount)}</td>
                  <td className="p-4 text-sm text-[#1A3C34]/60">{inv.date}</td>
                  <td className="p-4 text-sm text-[#1A3C34]/60">{inv.dueDate}</td>
                  <td className="p-4"><StatusBadge status={inv.status} /></td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button className="p-2 hover:bg-[#1A3C34]/5 rounded-lg"><Eye className="w-4 h-4 text-[#1A3C34]/60" /></button>
                      <button className="p-2 hover:bg-[#1A3C34]/5 rounded-lg"><PencilSimple className="w-4 h-4 text-[#1A3C34]/60" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ===== EXPENSES VIEW =====
  const renderExpenses = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3C34]">Shpenzimet</h2>
          <p className="text-sm text-[#1A3C34]/60">Gjurmoni dhe menaxhoni shpenzimet</p>
        </div>
        <Button onClick={() => setShowExpenseModal(true)} className="bg-[#1A3C34] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Regjistro Shpenzim
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <QuickStat label="Shpenzime Totale" value={formatCurrency(totalExpenses)} icon={Wallet} color="danger" />
            <QuickStat label="Në Pritje Aprovimi" value={expenses.filter(e => e.status === 'pending').length} icon={Clock} color="warning" />
          </div>
          
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
            <div className="divide-y divide-[#1A3C34]/5">
              {expenses.map(exp => (
                <div key={exp.id} className="flex items-center justify-between p-4 hover:bg-[#F8F9FA]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E76F51]/10 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-[#E76F51]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1A3C34]">{exp.description}</p>
                      <p className="text-xs text-[#1A3C34]/50">{exp.date} • {exp.category}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono font-medium text-[#1A3C34]">{formatFullCurrency(exp.amount)}</span>
                    <StatusBadge status={exp.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
          <h3 className="font-semibold text-[#1A3C34] mb-4">Sipas Kategorisë</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={expensesByCategory} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {expensesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatFullCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );

  // ===== TRANSACTIONS VIEW =====
  const renderTransactions = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3C34]">Transaksionet</h2>
          <p className="text-sm text-[#1A3C34]/60">Të gjitha transaksionet bankare</p>
        </div>
        <Button onClick={() => setShowTransactionModal(true)} className="bg-[#1A3C34] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Shto Transaksion
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickStat label="Totali Hyrje" value={formatCurrency(transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0))} icon={TrendUp} color="success" />
        <QuickStat label="Totali Dalje" value={formatCurrency(Math.abs(transactions.filter(t => t.amount < 0).reduce((s, t) => s + t.amount, 0)))} icon={TrendDown} color="danger" />
        <QuickStat label="Balanca Neto" value={formatCurrency(transactions.reduce((s, t) => s + t.amount, 0))} icon={Bank} color="default" />
      </div>

      <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
        <div className="divide-y divide-[#1A3C34]/5">
          {transactions.map(trx => (
            <div key={trx.id} className="flex items-center justify-between p-4 hover:bg-[#F8F9FA]">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${trx.amount >= 0 ? 'bg-[#2A9D8F]/10' : 'bg-[#E76F51]/10'}`}>
                  {trx.amount >= 0 ? <TrendUp className="w-5 h-5 text-[#2A9D8F]" /> : <TrendDown className="w-5 h-5 text-[#E76F51]" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-[#1A3C34]">{trx.description}</p>
                  <p className="text-xs text-[#1A3C34]/50">{trx.date} • {trx.account}</p>
                </div>
              </div>
              <span className={`font-mono text-lg font-medium ${trx.amount >= 0 ? 'text-[#2A9D8F]' : 'text-[#E76F51]'}`}>
                {trx.amount >= 0 ? '+' : ''}{formatFullCurrency(trx.amount)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ===== CLIENTS VIEW =====
  const renderClients = () => {
    const displayClients = clientsFromDB.length > 0 ? clientsFromDB : clients;
    
    // If a client is selected, show the detail view
    if (selectedClient && clientDetail) {
      const { client, invoices: clientInvoices, expenses: clientExpenses, workers: clientWorkers, summary } = clientDetail;
      
      return (
        <div className="space-y-6">
          {/* Back button and header */}
          <div className="flex items-center justify-between">
            <button onClick={handleCloseClientDetail} className="flex items-center gap-2 text-[#1A3C34]/60 hover:text-[#1A3C34]">
              <CaretLeft className="w-5 h-5" />
              <span>Kthehu te Klientët</span>
            </button>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleDeleteClient(client.client_id)}>
                <Trash className="w-4 h-4 mr-2 text-[#E76F51]" />
                Fshi
              </Button>
            </div>
          </div>

          {/* Client Header */}
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-6">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white text-2xl font-bold">
                {client.name?.charAt(0) || 'K'}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-semibold text-[#1A3C34]">{client.name}</h2>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-[#1A3C34]/60">
                  {client.email && <span className="flex items-center gap-1"><Envelope className="w-4 h-4" />{client.email}</span>}
                  {client.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{client.phone}</span>}
                  {client.address && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{client.address}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <QuickStat label="Fatura Totale" value={formatCurrency(summary.total_invoiced)} icon={Invoice} color="default" />
            <QuickStat label="E Paguar" value={formatCurrency(summary.total_paid)} icon={Check} color="success" />
            <QuickStat label="Në Pritje" value={formatCurrency(summary.pending_amount)} icon={Clock} color="warning" />
            <QuickStat label="Shpenzime" value={formatCurrency(summary.total_expenses)} icon={Wallet} color="danger" />
            <QuickStat label="TVSH (18%)" value={formatCurrency(summary.tax_amount)} icon={Percent} color="default" />
            <QuickStat label="Fitim Neto" value={formatCurrency(summary.net_profit)} icon={TrendUp} color={summary.net_profit >= 0 ? 'success' : 'danger'} />
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-4 text-center">
              <div className="text-3xl font-bold text-[#1A3C34]">{summary.invoice_count}</div>
              <div className="text-sm text-[#1A3C34]/60">Fatura</div>
            </div>
            <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-4 text-center">
              <div className="text-3xl font-bold text-[#1A3C34]">{summary.expense_count}</div>
              <div className="text-sm text-[#1A3C34]/60">Shpenzime</div>
            </div>
            <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-4 text-center">
              <div className="text-3xl font-bold text-[#1A3C34]">{summary.worker_count}</div>
              <div className="text-sm text-[#1A3C34]/60">Punëtorë</div>
            </div>
          </div>

          {/* Invoices Section */}
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
            <div className="p-4 border-b border-[#1A3C34]/5 flex items-center justify-between">
              <h3 className="font-semibold text-[#1A3C34]">Faturat</h3>
              <Button size="sm" onClick={() => setShowClientInvoiceModal(true)} className="bg-[#1A3C34]">
                <Plus className="w-4 h-4 mr-1" /> Faturë e Re
              </Button>
            </div>
            {clientInvoices.length > 0 ? (
              <table className="w-full">
                <thead className="bg-[#F8F9FA]">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Nr.</th>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Përshkrimi</th>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Data</th>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Afati</th>
                    <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Shuma</th>
                    <th className="text-center p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Statusi</th>
                    <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Veprime</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1A3C34]/5">
                  {clientInvoices.map(inv => (
                    <tr key={inv.invoice_id} className="hover:bg-[#F8F9FA]">
                      <td className="p-3 font-mono text-sm text-[#1A3C34]">{inv.invoice_number}</td>
                      <td className="p-3 text-sm text-[#1A3C34]">{inv.description || '-'}</td>
                      <td className="p-3 text-sm text-[#1A3C34]/60">{inv.date}</td>
                      <td className="p-3 text-sm text-[#1A3C34]/60">{inv.due_date}</td>
                      <td className="p-3 text-right font-mono font-medium text-[#1A3C34]">{formatFullCurrency(inv.amount)}</td>
                      <td className="p-3 text-center">
                        <Select value={inv.status} onValueChange={(v) => handleUpdateInvoiceStatus(inv.invoice_id, v)}>
                          <SelectTrigger className="w-28 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Në Pritje</SelectItem>
                            <SelectItem value="paid">E Paguar</SelectItem>
                            <SelectItem value="overdue">E Vonuar</SelectItem>
                            <SelectItem value="cancelled">Anuluar</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDeleteClientInvoice(inv.invoice_id)} className="p-1.5 hover:bg-[#E76F51]/10 rounded">
                          <Trash className="w-4 h-4 text-[#E76F51]" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-[#1A3C34]/50">
                <Invoice className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Ende pa fatura për këtë klient</p>
              </div>
            )}
          </div>

          {/* Expenses Section */}
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
            <div className="p-4 border-b border-[#1A3C34]/5 flex items-center justify-between">
              <h3 className="font-semibold text-[#1A3C34]">Shpenzimet</h3>
              <Button size="sm" onClick={() => setShowClientExpenseModal(true)} className="bg-[#1A3C34]">
                <Plus className="w-4 h-4 mr-1" /> Shpenzim i Ri
              </Button>
            </div>
            {clientExpenses.length > 0 ? (
              <table className="w-full">
                <thead className="bg-[#F8F9FA]">
                  <tr>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Data</th>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Përshkrimi</th>
                    <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Kategoria</th>
                    <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Shuma</th>
                    <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Veprime</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1A3C34]/5">
                  {clientExpenses.map(exp => (
                    <tr key={exp.expense_id} className="hover:bg-[#F8F9FA]">
                      <td className="p-3 text-sm text-[#1A3C34]/60">{exp.date}</td>
                      <td className="p-3 text-sm text-[#1A3C34]">{exp.description}</td>
                      <td className="p-3"><span className="px-2 py-1 rounded-full text-xs bg-[#1A3C34]/10 text-[#1A3C34]">{exp.category}</span></td>
                      <td className="p-3 text-right font-mono text-[#E76F51]">{formatFullCurrency(exp.amount)}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDeleteClientExpense(exp.expense_id)} className="p-1.5 hover:bg-[#E76F51]/10 rounded">
                          <Trash className="w-4 h-4 text-[#E76F51]" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-[#1A3C34]/50">
                <Wallet className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Ende pa shpenzime për këtë klient</p>
              </div>
            )}
          </div>

          {/* Workers Section */}
          <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
            <div className="p-4 border-b border-[#1A3C34]/5">
              <h3 className="font-semibold text-[#1A3C34]">Punëtorët e Caktuar</h3>
            </div>
            {clientWorkers.length > 0 ? (
              <div className="divide-y divide-[#1A3C34]/5">
                {clientWorkers.map(w => (
                  <div key={w.worker_id} className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-medium">
                        {w.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-[#1A3C34]">{w.name}</p>
                        <p className="text-sm text-[#1A3C34]/60">{w.role}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[#1A3C34]">{formatFullCurrency(w.monthly_wage)}/muaj</p>
                      {w.bonus > 0 && <p className="text-xs text-[#2A9D8F]">+{formatFullCurrency(w.bonus)} bonus</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-[#1A3C34]/50">
                <Briefcase className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Asnjë punëtor i caktuar për këtë klient</p>
              </div>
            )}
          </div>

          {/* Create Invoice Modal */}
          <Dialog open={showClientInvoiceModal} onOpenChange={setShowClientInvoiceModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Krijo Faturë të Re</DialogTitle>
                <DialogDescription>Krijoni faturë për {client.name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Shuma ({selectedCurrency}) *</Label>
                  <Input type="number" value={newClientInvoice.amount} onChange={(e) => setNewClientInvoice({...newClientInvoice, amount: e.target.value})} placeholder="0.00" />
                </div>
                <div>
                  <Label>Përshkrimi</Label>
                  <Input value={newClientInvoice.description} onChange={(e) => setNewClientInvoice({...newClientInvoice, description: e.target.value})} placeholder="Përshkrim i faturës..." />
                </div>
                <div>
                  <Label>Afati i Pagesës</Label>
                  <Input type="date" value={newClientInvoice.due_date} onChange={(e) => setNewClientInvoice({...newClientInvoice, due_date: e.target.value})} />
                </div>
                <div>
                  <Label>Statusi</Label>
                  <Select value={newClientInvoice.status} onValueChange={(v) => setNewClientInvoice({...newClientInvoice, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Në Pritje</SelectItem>
                      <SelectItem value="paid">E Paguar</SelectItem>
                      <SelectItem value="overdue">E Vonuar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreateClientInvoice} className="w-full bg-[#1A3C34]">Krijo Faturë</Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Create Expense Modal */}
          <Dialog open={showClientExpenseModal} onOpenChange={setShowClientExpenseModal}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Regjistro Shpenzim</DialogTitle>
                <DialogDescription>Regjistroni shpenzim për {client.name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Përshkrimi *</Label>
                  <Input value={newClientExpense.description} onChange={(e) => setNewClientExpense({...newClientExpense, description: e.target.value})} placeholder="Përshkrim i shpenzimit..." />
                </div>
                <div>
                  <Label>Kategoria</Label>
                  <Select value={newClientExpense.category} onValueChange={(v) => setNewClientExpense({...newClientExpense, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="supplies">Furnizime</SelectItem>
                      <SelectItem value="travel">Udhëtime</SelectItem>
                      <SelectItem value="meals">Ushqim</SelectItem>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="rent">Qira</SelectItem>
                      <SelectItem value="utilities">Shërbime</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="other">Tjetër</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Shuma ({selectedCurrency}) *</Label>
                  <Input type="number" value={newClientExpense.amount} onChange={(e) => setNewClientExpense({...newClientExpense, amount: e.target.value})} placeholder="0.00" />
                </div>
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={newClientExpense.date} onChange={(e) => setNewClientExpense({...newClientExpense, date: e.target.value})} />
                </div>
                <Button onClick={handleCreateClientExpense} className="w-full bg-[#1A3C34]">Regjistro Shpenzim</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    // Client List View
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[#1A3C34]">Klientët</h2>
            <p className="text-sm text-[#1A3C34]/60">Menaxhoni klientët dhe kontaktet</p>
          </div>
          <Button onClick={() => setShowClientModal(true)} className="bg-[#1A3C34] text-white">
            <Plus className="w-4 h-4 mr-2" />
            Shto Klient
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <QuickStat label="Klientë Totalë" value={displayClients.length} icon={Users} color="default" />
          <QuickStat label="Të Faturuar" value={formatCurrency(displayClients.reduce((s, c) => s + (c.total_invoiced || c.balance || 0), 0))} icon={Invoice} color="default" />
          <QuickStat label="Të Paguar" value={formatCurrency(displayClients.reduce((s, c) => s + (c.total_paid || 0), 0))} icon={Check} color="success" />
          <QuickStat label="Në Pritje" value={formatCurrency(displayClients.reduce((s, c) => s + (c.pending_amount || c.balance || 0), 0))} icon={Clock} color="warning" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayClients.map(client => (
            <div 
              key={client.client_id || client.id} 
              onClick={() => handleClientCardClick(client)}
              className="bg-white rounded-xl border border-[#1A3C34]/5 p-4 hover:border-[#1A3C34]/20 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-bold text-lg">
                  {client.name?.charAt(0) || 'K'}
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#1A3C34]/50">{client.invoice_count || 0} fatura</p>
                  <p className="text-xs text-[#1A3C34]/50">{client.worker_count || 0} punëtorë</p>
                </div>
              </div>
              <h3 className="font-semibold text-[#1A3C34] mb-1 group-hover:text-[#2A9D8F] transition-colors">{client.name}</h3>
              {client.email && <p className="text-xs text-[#1A3C34]/50 mb-1">{client.email}</p>}
              {client.phone && <p className="text-xs text-[#1A3C34]/50 mb-2">{client.phone}</p>}
              
              <div className="grid grid-cols-2 gap-2 pt-3 border-t border-[#1A3C34]/5 mt-2">
                <div>
                  <p className="text-xs text-[#1A3C34]/50">Të Faturuar</p>
                  <p className="font-mono font-medium text-[#1A3C34]">{formatCurrency(client.total_invoiced || client.balance || 0)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#1A3C34]/50">Fitimi</p>
                  <p className={`font-mono font-medium ${(client.net_profit || 0) >= 0 ? 'text-[#2A9D8F]' : 'text-[#E76F51]'}`}>
                    {formatCurrency(client.net_profit || 0)}
                  </p>
                </div>
              </div>
              
              <div className="mt-3 flex items-center justify-center text-xs text-[#1A3C34]/40 group-hover:text-[#2A9D8F]">
                <span>Kliko për detaje</span>
                <CaretRight className="w-4 h-4" />
              </div>
            </div>
          ))}
        </div>

        {/* Add Client Modal */}
        <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Shto Klient të Ri</DialogTitle>
              <DialogDescription>Vendosni të dhënat e klientit</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Emri *</Label>
                <Input value={newClientData.name} onChange={(e) => setNewClientData({...newClientData, name: e.target.value})} placeholder="Emri i kompanisë ose personit" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newClientData.email} onChange={(e) => setNewClientData({...newClientData, email: e.target.value})} placeholder="email@shembull.com" />
              </div>
              <div>
                <Label>Telefoni</Label>
                <Input value={newClientData.phone} onChange={(e) => setNewClientData({...newClientData, phone: e.target.value})} placeholder="+383 44 xxx xxx" />
              </div>
              <div>
                <Label>Adresa</Label>
                <Input value={newClientData.address} onChange={(e) => setNewClientData({...newClientData, address: e.target.value})} placeholder="Adresa e biznesit" />
              </div>
              <div>
                <Label>Shënime</Label>
                <Input value={newClientData.notes} onChange={(e) => setNewClientData({...newClientData, notes: e.target.value})} placeholder="Shënime shtesë..." />
              </div>
              <Button onClick={handleCreateClient} className="w-full bg-[#1A3C34]">Shto Klient</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ===== REPORTS VIEW =====
  const renderReports = () => {
    const reports = [
      { id: 'income', icon: ChartBar, title: 'Pasqyra e të Ardhurave', desc: 'Raport i detajuar i të ardhurave dhe shpenzimeve' },
      { id: 'balance', icon: Bank, title: 'Bilanci', desc: 'Gjendja e aseteve, detyrimeve dhe kapitalit' },
      { id: 'cashflow', icon: ArrowsLeftRight, title: 'Rrjedha e Parasë', desc: 'Analiza e hyrjeve dhe daljeve të parasë' },
      { id: 'clients', icon: Users, title: 'Raport Klientësh', desc: 'Analiza e faturave dhe pagesave sipas klientit' },
      { id: 'expenses', icon: Wallet, title: 'Raport Shpenzimesh', desc: 'Shpenzime sipas kategorisë dhe periudhës' },
      { id: 'tax', icon: Calculator, title: 'Raport Tatimor', desc: 'Përmbledhje për qëllime tatimore' },
    ];

    const renderReportContent = () => {
      switch (activeReport) {
        case 'income':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Pasqyra e të Ardhurave</h3>
                  <p className="text-sm text-[#1A3C34]/60">Periudha: Janar 2025</p>
                </div>
                {renderReportExportMenu('income')}
              </div>
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                <table className="w-full">
                  <tbody className="divide-y divide-[#1A3C34]/5">
                    <tr className="bg-[#1A3C34]/5">
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">Të Ardhurat</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Të ardhura nga shërbimet</td>
                      <td className="p-4 text-right font-mono text-[#1A3C34]">{formatFullCurrency(financialData.incomeStatement.revenue)}</td>
                    </tr>
                    <tr className="bg-[#1A3C34]/5">
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">Kostot</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Kosto e mallrave të shitura</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">-{formatFullCurrency(financialData.incomeStatement.costOfGoodsSold)}</td>
                    </tr>
                    <tr className="border-t-2 border-[#1A3C34]/10">
                      <td className="p-4 font-medium text-[#1A3C34]">Fitimi Bruto</td>
                      <td className="p-4 text-right font-mono font-medium text-[#2A9D8F]">{formatFullCurrency(financialData.incomeStatement.grossProfit)}</td>
                    </tr>
                    <tr className="bg-[#1A3C34]/5">
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">Shpenzimet Operative</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Shpenzime administrative</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">-{formatFullCurrency(financialData.incomeStatement.operatingExpenses)}</td>
                    </tr>
                    <tr className="border-t-2 border-[#1A3C34]/10">
                      <td className="p-4 font-medium text-[#1A3C34]">Fitimi Operativ</td>
                      <td className="p-4 text-right font-mono font-medium text-[#2A9D8F]">{formatFullCurrency(financialData.incomeStatement.operatingIncome)}</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Interesa</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">-{formatFullCurrency(financialData.incomeStatement.interestExpense)}</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Tatime (15%)</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">-{formatFullCurrency(financialData.incomeStatement.taxExpense)}</td>
                    </tr>
                    <tr className="bg-[#2A9D8F]/10 border-t-2 border-[#2A9D8F]">
                      <td className="p-4 font-semibold text-[#1A3C34]">Fitimi Neto</td>
                      <td className="p-4 text-right font-mono font-bold text-[#2A9D8F] text-lg">{formatFullCurrency(financialData.incomeStatement.netIncome)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );

        case 'balance':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Bilanci Kontabël</h3>
                  <p className="text-sm text-[#1A3C34]/60">Deri më: 31 Janar 2025</p>
                </div>
                {renderReportExportMenu('balance')}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Assets */}
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                  <div className="p-4 bg-[#2A9D8F]/10 border-b border-[#1A3C34]/5">
                    <h4 className="font-semibold text-[#1A3C34]">ASETET</h4>
                  </div>
                  <div className="divide-y divide-[#1A3C34]/5">
                    <div className="p-4 bg-[#F8F9FA]">
                      <span className="text-sm font-medium text-[#1A3C34]">Asete Rrjedhëse</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Para në dorë</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.cash)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Llogari të arkëtueshme</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.accountsReceivable)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Inventar</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.inventory)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#1A3C34]/5">
                      <span className="font-medium text-[#1A3C34]">Total Asete Rrjedhëse</span>
                      <span className="font-mono font-medium text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.totalCurrentAssets)}</span>
                    </div>
                    <div className="p-4 bg-[#F8F9FA]">
                      <span className="text-sm font-medium text-[#1A3C34]">Asete Fikse</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Pajisje & mobilje</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.propertyEquipment)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#2A9D8F]/10 border-t-2 border-[#2A9D8F]">
                      <span className="font-semibold text-[#1A3C34]">TOTAL ASETE</span>
                      <span className="font-mono font-bold text-[#2A9D8F]">{formatFullCurrency(financialData.balanceSheet.totalAssets)}</span>
                    </div>
                  </div>
                </div>

                {/* Liabilities & Equity */}
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                  <div className="p-4 bg-[#E76F51]/10 border-b border-[#1A3C34]/5">
                    <h4 className="font-semibold text-[#1A3C34]">DETYRIME & KAPITAL</h4>
                  </div>
                  <div className="divide-y divide-[#1A3C34]/5">
                    <div className="p-4 bg-[#F8F9FA]">
                      <span className="text-sm font-medium text-[#1A3C34]">Detyrime Rrjedhëse</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Llogari të pagueshme</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.accountsPayable)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Borxh afatshkurtër</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.shortTermDebt)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#1A3C34]/5">
                      <span className="font-medium text-[#1A3C34]">Total Detyrime Rrjedhëse</span>
                      <span className="font-mono font-medium text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.totalCurrentLiabilities)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Borxh afatgjatë</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.longTermDebt)}</span>
                    </div>
                    <div className="p-4 bg-[#F8F9FA]">
                      <span className="text-sm font-medium text-[#1A3C34]">Kapitali</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Kapitali i aksionerëve</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.shareholdersEquity)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Fitime të mbajtura</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.balanceSheet.retainedEarnings)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#2A9D8F]/10 border-t-2 border-[#2A9D8F]">
                      <span className="font-semibold text-[#1A3C34]">TOTAL DETYRIME & KAPITAL</span>
                      <span className="font-mono font-bold text-[#2A9D8F]">{formatFullCurrency(financialData.balanceSheet.totalAssets)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );

        case 'cashflow':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Pasqyra e Rrjedhës së Parasë</h3>
                  <p className="text-sm text-[#1A3C34]/60">Periudha: Janar 2025</p>
                </div>
                {renderReportExportMenu('cashflow')}
              </div>
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                <table className="w-full">
                  <tbody className="divide-y divide-[#1A3C34]/5">
                    <tr className="bg-[#2A9D8F]/10">
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">Aktivitetet Operative</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Fitimi neto</td>
                      <td className="p-4 text-right font-mono text-[#2A9D8F]">{formatFullCurrency(financialData.incomeStatement.netIncome)}</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Amortizim</td>
                      <td className="p-4 text-right font-mono text-[#1A3C34]">{formatFullCurrency(500)}</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Ndryshim në llogaritë e arkëtueshme</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">-{formatFullCurrency(totalPending * 0.3)}</td>
                    </tr>
                    <tr className="bg-[#1A3C34]/5">
                      <td className="p-4 font-medium text-[#1A3C34]">Rrjedha neto operative</td>
                      <td className="p-4 text-right font-mono font-medium text-[#2A9D8F]">{formatFullCurrency(financialData.cashFlow.operatingCashFlow)}</td>
                    </tr>
                    <tr className="bg-[#D4A373]/10">
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">Aktivitetet Investuese</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Blerje pajisjesh</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">{formatFullCurrency(financialData.cashFlow.investingCashFlow)}</td>
                    </tr>
                    <tr className="bg-[#E76F51]/10">
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">Aktivitetet Financuese</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Pagesa e borxhit</td>
                      <td className="p-4 text-right font-mono text-[#E76F51]">{formatFullCurrency(financialData.cashFlow.financingCashFlow)}</td>
                    </tr>
                    <tr className="border-t-2 border-[#1A3C34]/10">
                      <td className="p-4 font-medium text-[#1A3C34]">Ndryshimi neto në para</td>
                      <td className="p-4 text-right font-mono font-medium text-[#2A9D8F]">{formatFullCurrency(financialData.cashFlow.netChangeInCash)}</td>
                    </tr>
                    <tr className="hover:bg-[#F8F9FA]">
                      <td className="p-4 text-[#1A3C34]/70">Para në fillim të periudhës</td>
                      <td className="p-4 text-right font-mono text-[#1A3C34]">{formatFullCurrency(financialData.cashFlow.beginningCash)}</td>
                    </tr>
                    <tr className="bg-[#2A9D8F]/10 border-t-2 border-[#2A9D8F]">
                      <td className="p-4 font-semibold text-[#1A3C34]">Para në fund të periudhës</td>
                      <td className="p-4 text-right font-mono font-bold text-[#2A9D8F] text-lg">{formatFullCurrency(financialData.cashFlow.endingCash)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );

        case 'clients':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Raport Klientësh</h3>
                  <p className="text-sm text-[#1A3C34]/60">Analiza e faturave dhe pagesave sipas klientit</p>
                </div>
                {renderReportExportMenu('clients')}
              </div>
              <div className="grid grid-cols-3 gap-4">
                <QuickStat label="Klientë Totalë" value={clients.length} icon={Users} color="default" />
                <QuickStat label="Balanca e Hapur" value={formatCurrency(clients.reduce((s, c) => s + c.balance, 0))} icon={Clock} color="warning" />
                <QuickStat label="Total i Paguar" value={formatCurrency(clients.reduce((s, c) => s + (c.totalPaid || 0), 0))} icon={Check} color="success" />
              </div>
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F8F9FA]">
                    <tr>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Klienti</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Email</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Fatura</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Total Paguar</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Balanca</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1A3C34]/5">
                    {clients.map(client => (
                      <tr key={client.id} className="hover:bg-[#F8F9FA]">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-medium text-sm">
                              {client.name.charAt(0)}
                            </div>
                            <span className="font-medium text-[#1A3C34]">{client.name}</span>
                          </div>
                        </td>
                        <td className="p-4 text-sm text-[#1A3C34]/60">{client.email}</td>
                        <td className="p-4 text-right font-mono text-[#1A3C34]">{client.invoiceCount || 1}</td>
                        <td className="p-4 text-right font-mono text-[#2A9D8F]">{formatFullCurrency(client.totalPaid || 0)}</td>
                        <td className="p-4 text-right font-mono font-medium">
                          <span className={client.balance > 0 ? 'text-[#D4A373]' : 'text-[#1A3C34]'}>
                            {formatFullCurrency(client.balance)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#1A3C34]/5">
                    <tr>
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">TOTALI</td>
                      <td className="p-4 text-right font-mono font-medium text-[#1A3C34]">{clients.reduce((s, c) => s + (c.invoiceCount || 1), 0)}</td>
                      <td className="p-4 text-right font-mono font-bold text-[#2A9D8F]">{formatFullCurrency(clients.reduce((s, c) => s + (c.totalPaid || 0), 0))}</td>
                      <td className="p-4 text-right font-mono font-bold text-[#D4A373]">{formatFullCurrency(clients.reduce((s, c) => s + c.balance, 0))}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );

        case 'expenses':
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Raport Shpenzimesh</h3>
                  <p className="text-sm text-[#1A3C34]/60">Shpenzimet sipas kategorisë dhe periudhës</p>
                </div>
                {renderReportExportMenu('expenses')}
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
                  <h4 className="font-semibold text-[#1A3C34] mb-4">Shpenzimet sipas Kategorisë</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie 
                          data={expensesByCategory} 
                          cx="50%" 
                          cy="50%" 
                          innerRadius={50} 
                          outerRadius={80} 
                          dataKey="value" 
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} 
                          labelLine={false}
                        >
                          {expensesByCategory.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatFullCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                  <div className="p-4 border-b border-[#1A3C34]/5">
                    <h4 className="font-semibold text-[#1A3C34]">Detajet e Kategorive</h4>
                  </div>
                  <div className="divide-y divide-[#1A3C34]/5">
                    {expensesByCategory.map((cat, i) => (
                      <div key={i} className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          <span className="text-[#1A3C34]">{cat.name}</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono font-medium text-[#1A3C34]">{formatFullCurrency(cat.value)}</span>
                          <span className="text-xs text-[#1A3C34]/50 ml-2">
                            ({((cat.value / totalExpenses) * 100).toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className="p-4 flex items-center justify-between bg-[#1A3C34]/5">
                      <span className="font-semibold text-[#1A3C34]">TOTALI</span>
                      <span className="font-mono font-bold text-[#1A3C34]">{formatFullCurrency(totalExpenses)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                <div className="p-4 border-b border-[#1A3C34]/5">
                  <h4 className="font-semibold text-[#1A3C34]">Lista e Shpenzimeve</h4>
                </div>
                <table className="w-full">
                  <thead className="bg-[#F8F9FA]">
                    <tr>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Data</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Përshkrimi</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Kategoria</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Shuma</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1A3C34]/5">
                    {expenses.slice(0, 8).map(exp => (
                      <tr key={exp.id} className="hover:bg-[#F8F9FA]">
                        <td className="p-4 text-sm text-[#1A3C34]/60">{exp.date}</td>
                        <td className="p-4 text-sm text-[#1A3C34]">{exp.description}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 rounded-full text-xs bg-[#1A3C34]/10 text-[#1A3C34]">
                            {expensesByCategory.find(c => c.name.toLowerCase().includes(exp.category.slice(0, 3)))?.name || exp.category}
                          </span>
                        </td>
                        <td className="p-4 text-right font-mono text-[#E76F51]">{formatFullCurrency(exp.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );

        case 'tax':
          const taxableIncome = financialData.incomeStatement.operatingIncome - financialData.incomeStatement.interestExpense;
          const vatCollected = totalIncome * 0.18;
          const vatPaid = totalExpenses * 0.18;
          const vatPayable = vatCollected - vatPaid;
          
          return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Raport Tatimor</h3>
                  <p className="text-sm text-[#1A3C34]/60">Përmbledhje për qëllime tatimore - Q1 2025</p>
                </div>
                {renderReportExportMenu('tax')}
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <QuickStat label="Fitim i Tatueshëm" value={formatCurrency(taxableIncome)} icon={Calculator} color="default" />
                <QuickStat label="Tatim Fitimi (15%)" value={formatCurrency(taxableIncome * 0.15)} icon={Percent} color="warning" />
                <QuickStat label="TVSH për Pagesë" value={formatCurrency(vatPayable)} icon={Receipt} color="danger" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                  <div className="p-4 bg-[#1A3C34]/5 border-b border-[#1A3C34]/5">
                    <h4 className="font-semibold text-[#1A3C34]">Tatimi mbi Fitimin</h4>
                  </div>
                  <div className="divide-y divide-[#1A3C34]/5">
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Të ardhura totale</span>
                      <span className="font-mono text-[#1A3C34]">{formatFullCurrency(financialData.incomeStatement.revenue)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Shpenzime të zbritshme</span>
                      <span className="font-mono text-[#E76F51]">-{formatFullCurrency(totalExpenses)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">Interesa</span>
                      <span className="font-mono text-[#E76F51]">-{formatFullCurrency(financialData.incomeStatement.interestExpense)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#F8F9FA]">
                      <span className="font-medium text-[#1A3C34]">Fitim i tatueshëm</span>
                      <span className="font-mono font-medium text-[#1A3C34]">{formatFullCurrency(taxableIncome)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#E76F51]/10">
                      <span className="font-semibold text-[#1A3C34]">Tatim Fitimi (15%)</span>
                      <span className="font-mono font-bold text-[#E76F51]">{formatFullCurrency(taxableIncome * 0.15)}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                  <div className="p-4 bg-[#1A3C34]/5 border-b border-[#1A3C34]/5">
                    <h4 className="font-semibold text-[#1A3C34]">TVSH (18%)</h4>
                  </div>
                  <div className="divide-y divide-[#1A3C34]/5">
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">TVSH e mbledhur (shitjet)</span>
                      <span className="font-mono text-[#2A9D8F]">{formatFullCurrency(vatCollected)}</span>
                    </div>
                    <div className="p-4 flex justify-between">
                      <span className="text-[#1A3C34]/70">TVSH e paguar (blerjet)</span>
                      <span className="font-mono text-[#E76F51]">-{formatFullCurrency(vatPaid)}</span>
                    </div>
                    <div className="p-4 flex justify-between bg-[#D4A373]/10">
                      <span className="font-semibold text-[#1A3C34]">TVSH për pagesë</span>
                      <span className="font-mono font-bold text-[#D4A373]">{formatFullCurrency(vatPayable)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] rounded-xl p-6 text-white">
                <h4 className="font-semibold mb-4">Përmbledhje e Detyrimeve Tatimore</h4>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-white/70 text-sm">Tatim Fitimi</p>
                    <p className="text-2xl font-mono font-bold">{formatFullCurrency(taxableIncome * 0.15)}</p>
                    <p className="text-xs text-white/50 mt-1">Afati: 31 Mars 2025</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">TVSH për Pagesë</p>
                    <p className="text-2xl font-mono font-bold">{formatFullCurrency(vatPayable)}</p>
                    <p className="text-xs text-white/50 mt-1">Afati: 14 Shkurt 2025</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Total Detyrime</p>
                    <p className="text-2xl font-mono font-bold">{formatFullCurrency(taxableIncome * 0.15 + vatPayable)}</p>
                  </div>
                </div>
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div className="space-y-6">
        {activeReport ? (
          <>
            <button 
              onClick={() => setActiveReport(null)} 
              className="flex items-center gap-2 text-[#1A3C34]/60 hover:text-[#1A3C34] transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              <span>Kthehu te Raportet</span>
            </button>
            {renderReportContent()}
          </>
        ) : (
          <>
            <div>
              <h2 className="text-xl font-semibold text-[#1A3C34]">Raportet</h2>
              <p className="text-sm text-[#1A3C34]/60">Raporte financiare dhe analiza</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reports.map((report) => (
                <div 
                  key={report.id} 
                  onClick={() => setActiveReport(report.id)}
                  className="bg-white rounded-xl border border-[#1A3C34]/5 p-5 hover:border-[#1A3C34]/15 transition-all cursor-pointer group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#1A3C34]/5 flex items-center justify-center mb-4 group-hover:bg-[#1A3C34]/10 transition-colors">
                    <report.icon className="w-6 h-6 text-[#1A3C34]" weight="duotone" />
                  </div>
                  <h3 className="font-medium text-[#1A3C34] mb-1">{report.title}</h3>
                  <p className="text-sm text-[#1A3C34]/60">{report.desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
              <h3 className="font-semibold text-[#1A3C34] mb-4">Trendi Mujor ({selectedCurrency})</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: '#1A3C34', fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fill: '#1A3C34', fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => formatCurrency(v)} />
                    <Tooltip formatter={(value) => formatFullCurrency(value)} />
                    <Bar dataKey="income" fill="#2A9D8F" radius={[4, 4, 0, 0]} name="Të Ardhura" />
                    <Bar dataKey="expenses" fill="#E76F51" radius={[4, 4, 0, 0]} name="Shpenzime" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // ===== AI INSIGHTS VIEW =====
  const renderInsights = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#1A3C34]">Njohuri AI</h2>
        <p className="text-sm text-[#1A3C34]/60">Analiza inteligjente e të dhënave tuaja</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-[#D4A373]/10 to-[#D4A373]/5 rounded-xl border border-[#D4A373]/20 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#D4A373]/20 flex items-center justify-center">
              <Sparkle className="w-5 h-5 text-[#D4A373]" weight="fill" />
            </div>
            <h3 className="font-semibold text-[#1A3C34]">Analiza e Rrjedhës së Parasë</h3>
          </div>
          <p className="text-sm text-[#1A3C34]/70 mb-4">
            Bazuar në të dhënat tuaja, rrjedha e parasë tregon trend pozitiv me rritje 15% krahasuar me muajin e kaluar. 
            Rekomandohet të rrisni rezervat e parasë për të përballuar periudhën e ngadaltë të verës.
          </p>
          <Badge className="bg-[#2A9D8F]/10 text-[#2A9D8F]">Trend Pozitiv</Badge>
        </div>

        <div className="bg-gradient-to-br from-[#E76F51]/10 to-[#E76F51]/5 rounded-xl border border-[#E76F51]/20 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#E76F51]/20 flex items-center justify-center">
              <Warning className="w-5 h-5 text-[#E76F51]" weight="fill" />
            </div>
            <h3 className="font-semibold text-[#1A3C34]">Paralajmërime</h3>
          </div>
          <p className="text-sm text-[#1A3C34]/70 mb-4">
            Keni {invoices.filter(i => i.status === 'overdue').length} fatura të vonuara me vlerë totale {formatFullCurrency(invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + i.amount, 0))}. 
            Rekomandohet të kontaktoni klientët për të ndjekur pagesat.
          </p>
          <Badge className="bg-[#E76F51]/10 text-[#E76F51]">Kërkon Vëmendje</Badge>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-6">
        <h3 className="font-semibold text-[#1A3C34] mb-4">Rekomandimet e AI</h3>
        <div className="space-y-4">
          {[
            { icon: Check, text: 'Shpenzimet operative janë brenda normës - vazhdoni monitorimin', color: 'success' },
            { icon: Clock, text: 'Konsideroni të automatizoni faturimin për të kursyer kohë', color: 'warning' },
            { icon: ChartLineUp, text: 'Rritja e të ardhurave mund të mbështesë zgjerimin e stafit', color: 'success' },
            { icon: Calculator, text: 'Përgatituni për deklaratën tatimore - mblidhni dokumentet', color: 'default' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[#F8F9FA]">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                item.color === 'success' ? 'bg-[#2A9D8F]/10' : item.color === 'warning' ? 'bg-[#D4A373]/10' : 'bg-[#1A3C34]/10'
              }`}>
                <item.icon className={`w-4 h-4 ${
                  item.color === 'success' ? 'text-[#2A9D8F]' : item.color === 'warning' ? 'text-[#D4A373]' : 'text-[#1A3C34]'
                }`} weight="bold" />
              </div>
              <p className="text-sm text-[#1A3C34]/80">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ===== CLOUD DOCUMENTS VIEW (dedicated storage space) =====
  const docTypeLabels = {
    invoice: 'Faturë',
    receipt: 'Faturë Blerje',
    bank_statement: 'Pasqyrë Bankare',
    contract: 'Kontratë',
    tax_document: 'Dokument Tatimor',
    other: 'Tjetër',
  };

  const renderDocumentCard = (doc) => (
    <div key={doc.document_id} className="bg-white rounded-xl border border-[#1A3C34]/5 p-4 hover:border-[#1A3C34]/15 hover:shadow-sm transition-all" data-testid={`document-card-${doc.document_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
          <File className="w-5 h-5 text-[#2A9D8F]" weight={doc.file_path ? 'fill' : 'regular'} />
        </div>
        <div className="flex items-center gap-1">
          {(doc.file_path || doc.file_name) && (
            <>
              <button onClick={() => handlePreviewDocument(doc)} className="p-1.5 hover:bg-[#1A3C34]/5 rounded" title="Shiko" data-testid={`preview-doc-${doc.document_id}`}>
                <Eye className="w-4 h-4 text-[#1A3C34]" />
              </button>
              <button onClick={() => handleDownloadDocument(doc)} className="p-1.5 hover:bg-[#1A3C34]/5 rounded" title="Shkarko" data-testid={`download-doc-${doc.document_id}`}>
                <DownloadSimple className="w-4 h-4 text-[#1A3C34]" />
              </button>
            </>
          )}
          <button onClick={() => handleDeleteDocument(doc.document_id)} className="p-1.5 hover:bg-[#E76F51]/10 rounded" title="Fshi" data-testid={`delete-doc-${doc.document_id}`}>
            <Trash className="w-4 h-4 text-[#E76F51]" />
          </button>
        </div>
      </div>
      <h4 className="font-medium text-[#1A3C34] mb-1 truncate">{doc.name}</h4>
      <div className="flex items-center gap-2 mb-2">
        <span className="px-2 py-0.5 rounded-full text-xs bg-[#1A3C34]/5 text-[#1A3C34]/70">{docTypeLabels[doc.document_type] || doc.document_type}</span>
        {doc.file_size ? <span className="text-xs text-[#1A3C34]/40">{formatFileSize(doc.file_size)}</span> : null}
      </div>
      {doc.file_name && (
        <p className="text-xs text-[#2A9D8F] mb-1 flex items-center gap-1 truncate">
          <Check className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{doc.file_name}</span>
        </p>
      )}
      {doc.notes && <p className="text-xs text-[#1A3C34]/60 line-clamp-2">{doc.notes}</p>}
      <p className="text-xs text-[#1A3C34]/40 mt-2">{doc.created_at?.split('T')[0]}</p>
    </div>
  );

  const renderDocuments = () => {
    const search = documentSearch.trim().toLowerCase();
    const filtered = bookkeepingDocuments.filter((d) => {
      const matchesType = documentFilter === 'all' || d.document_type === documentFilter;
      const matchesSearch = !search ||
        (d.name || '').toLowerCase().includes(search) ||
        (d.file_name || '').toLowerCase().includes(search);
      return matchesType && matchesSearch;
    });

    const usedMb = bookkeepingStats?.storage_used_mb || 0;
    const maxMb = bookkeepingStats?.max_storage_mb || 50;
    const storagePct = maxMb >= 51200 ? Math.min(100, (usedMb / maxMb) * 100) : Math.min(100, (usedMb / maxMb) * 100);
    const docsCount = bookkeepingStats?.documents || 0;
    const maxDocs = bookkeepingStats?.max_documents || 10;
    const fmtStorage = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB` : `${mb} MB`);

    return (
      <div className="space-y-6" data-testid="documents-cloud-view">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-[#1A3C34]">Hapësira Cloud</h2>
            <p className="text-sm text-[#1A3C34]/60">Të gjitha dokumentet tuaja të ngarkuara në një vend</p>
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-[#1A3C34]/10" data-testid="export-documents-btn" disabled={bookkeepingDocuments.length === 0}>
                  <Export className="w-4 h-4 mr-2" />Eksporto
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExportDocumentsList('pdf')}>PDF</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportDocumentsList('excel')}>Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportDocumentsList('csv')}>CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setShowDocumentModal(true)} className="bg-[#1A3C34]" data-testid="cloud-upload-document-btn">
              <CloudArrowUp className="w-4 h-4 mr-2" />Ngarko Dokument
            </Button>
          </div>
        </div>

        {/* Storage usage card */}
        <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#1A3C34]">Hapësira e Përdorur</span>
                <span className="text-sm text-[#1A3C34]/60">{fmtStorage(usedMb)} / {fmtStorage(maxMb)}</span>
              </div>
              <Progress value={storagePct} className="h-2.5" data-testid="storage-progress" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[#1A3C34]">Numri i Dokumenteve</span>
                <span className="text-sm text-[#1A3C34]/60">{docsCount} / {maxDocs >= 9999 ? '∞' : maxDocs}</span>
              </div>
              <Progress value={maxDocs >= 9999 ? 0 : Math.min(100, (docsCount / maxDocs) * 100)} className="h-2.5" />
            </div>
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A3C34]/40" />
            <Input
              placeholder="Kërko sipas emrit ose skedarit..."
              value={documentSearch}
              onChange={(e) => setDocumentSearch(e.target.value)}
              className="pl-9 border-[#1A3C34]/10"
              data-testid="document-search-input"
            />
          </div>
          <Select value={documentFilter} onValueChange={setDocumentFilter}>
            <SelectTrigger className="w-full sm:w-56 border-[#1A3C34]/10" data-testid="document-filter-select">
              <FunnelSimple className="w-4 h-4 mr-2 text-[#1A3C34]/50" />
              <SelectValue placeholder="Filtro sipas llojit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha llojet</SelectItem>
              {Object.entries(docTypeLabels).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Documents grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((doc) => renderDocumentCard(doc))}
          {filtered.length === 0 && (
            <div className="col-span-full bg-white rounded-xl border border-dashed border-[#1A3C34]/15 p-12 text-center text-[#1A3C34]/50">
              <CloudArrowUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>{bookkeepingDocuments.length === 0 ? 'Ende pa dokumente të ngarkuara' : 'Asnjë dokument nuk përputhet me kërkimin'}</p>
            </div>
          )}
        </div>

        {/* Document Upload Modal for Documents Nav */}
        <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ngarko Dokument</DialogTitle>
              <DialogDescription>Ruani dokumentet tuaja në cloud ({bookkeepingStats?.documents_remaining || 0} të mbetura)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Emri i Dokumentit *</Label>
                <Input value={newDocument.name} onChange={(e) => setNewDocument({...newDocument, name: e.target.value})} placeholder="p.sh. Faturë Janar 2025" data-testid="doc-name-input" />
              </div>
              <div>
                <Label>Lloji i Dokumentit</Label>
                <Select value={newDocument.document_type} onValueChange={(v) => setNewDocument({...newDocument, document_type: v})}>
                  <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Faturë</SelectItem>
                    <SelectItem value="receipt">Faturë Blerje</SelectItem>
                    <SelectItem value="bank_statement">Pasqyrë Bankare</SelectItem>
                    <SelectItem value="contract">Kontratë</SelectItem>
                    <SelectItem value="tax_document">Dokument Tatimor</SelectItem>
                    <SelectItem value="other">Tjetër</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Skedari (opsional)</Label>
                <div className={`border-2 border-dashed rounded-xl p-4 text-center cursor-pointer ${newDocument.file ? 'border-[#2A9D8F] bg-[#2A9D8F]/5' : 'border-[#1A3C34]/20 hover:border-[#1A3C34]/40'}`}>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="document-file-upload-nav"
                    accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.doc,.docx,.txt"
                    data-testid="doc-file-input"
                  />
                  <label htmlFor="document-file-upload-nav" className="cursor-pointer">
                    {newDocument.file ? (
                      <div className="flex items-center justify-center gap-2 text-[#2A9D8F]">
                        <Check className="w-5 h-5" />
                        <span className="font-medium truncate max-w-[200px]">{newDocument.file.name}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-[#1A3C34]/50">
                        <CloudArrowUp className="w-6 h-6" />
                        <span className="text-sm">Kliko për të zgjedhur skedar</span>
                        <span className="text-xs">PDF, Images, Excel, Word (Max 10MB)</span>
                      </div>
                    )}
                  </label>
                </div>
              </div>
              <div>
                <Label>Shënime (opsionale)</Label>
                <Textarea value={newDocument.notes} onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})} placeholder="Shënime për këtë dokument..." rows={2} />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowDocumentModal(false)}>Anulo</Button>
              <Button onClick={handleCreateDocument} disabled={uploadingDocument || !newDocument.name.trim()} className="bg-[#1A3C34]" data-testid="doc-save-btn">
                {uploadingDocument ? (
                  <><span className="animate-spin mr-2">⏳</span>Duke ngarkuar...</>
                ) : (
                  <><CloudArrowUp className="w-4 h-4 mr-2" />Ruaj Dokumentin</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };


  // ===== BOOK-KEEPING VIEW =====
  const renderBookkeeping = () => {
    const accountTypes = {
      asset: { name: 'Aset', color: '#2A9D8F' },
      liability: { name: 'Detyrim', color: '#E76F51' },
      equity: { name: 'Kapital', color: '#264653' },
      revenue: { name: 'Të Ardhura', color: '#1A3C34' },
      expense: { name: 'Shpenzim', color: '#D4A373' }
    };

    const documentTypes = {
      invoice: 'Faturë',
      receipt: 'Faturë Blerje',
      bank_statement: 'Pasqyrë Bankare',
      contract: 'Kontratë',
      tax_document: 'Dokument Tatimor',
      other: 'Tjetër'
    };

    const renderBookkeepingContent = () => {
      switch (bookkeepingView) {
        case 'ledger':
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#1A3C34]">Libri i Madh (Ledger)</h3>
                <Button onClick={() => setShowLedgerModal(true)} className="bg-[#1A3C34]">
                  <Plus className="w-4 h-4 mr-2" />Regjistrim i Ri
                </Button>
              </div>
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                {ledgerEntries.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-[#F8F9FA]">
                      <tr>
                        <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Data</th>
                        <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Llogaria</th>
                        <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Lloji</th>
                        <th className="text-left p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Përshkrimi</th>
                        <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Debit</th>
                        <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Kredit</th>
                        <th className="text-right p-3 text-xs font-medium text-[#1A3C34]/60 uppercase">Veprime</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1A3C34]/5">
                      {ledgerEntries.map(entry => (
                        <tr key={entry.entry_id} className="hover:bg-[#F8F9FA]">
                          <td className="p-3 text-sm text-[#1A3C34]/60">{entry.date}</td>
                          <td className="p-3 font-medium text-[#1A3C34]">{entry.account_name}</td>
                          <td className="p-3"><span className="px-2 py-1 rounded-full text-xs" style={{ backgroundColor: `${accountTypes[entry.account_type]?.color}20`, color: accountTypes[entry.account_type]?.color }}>{accountTypes[entry.account_type]?.name}</span></td>
                          <td className="p-3 text-sm text-[#1A3C34]/70">{entry.description || '-'}</td>
                          <td className="p-3 text-right font-mono text-[#1A3C34]">{entry.debit > 0 ? formatFullCurrency(entry.debit) : '-'}</td>
                          <td className="p-3 text-right font-mono text-[#1A3C34]">{entry.credit > 0 ? formatFullCurrency(entry.credit) : '-'}</td>
                          <td className="p-3 text-right">
                            <button onClick={() => handleDeleteLedgerEntry(entry.entry_id)} className="p-1.5 hover:bg-[#E76F51]/10 rounded"><Trash className="w-4 h-4 text-[#E76F51]" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-12 text-center text-[#1A3C34]/50">
                    <Table className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Ende pa regjistrime në libër</p>
                  </div>
                )}
              </div>
            </div>
          );

        case 'journal':
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#1A3C34]">Ditari i Përgjithshëm</h3>
                <Button onClick={() => setShowJournalModal(true)} className="bg-[#1A3C34]">
                  <Plus className="w-4 h-4 mr-2" />Ditar i Ri
                </Button>
              </div>
              <div className="space-y-4">
                {journalEntries.length > 0 ? journalEntries.map(journal => (
                  <div key={journal.journal_id} className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                    <div className="p-4 flex items-center justify-between border-b border-[#1A3C34]/5">
                      <div>
                        <span className="font-mono text-sm text-[#2A9D8F] font-medium">{journal.journal_number}</span>
                        <span className="text-[#1A3C34]/40 mx-2">•</span>
                        <span className="text-sm text-[#1A3C34]/60">{journal.date}</span>
                      </div>
                      <button onClick={() => handleDeleteJournalEntry(journal.journal_id)} className="p-1.5 hover:bg-[#E76F51]/10 rounded"><Trash className="w-4 h-4 text-[#E76F51]" /></button>
                    </div>
                    <div className="p-4">
                      <p className="font-medium text-[#1A3C34] mb-3">{journal.description}</p>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[#1A3C34]/50">
                            <th className="text-left py-1">Llogaria</th>
                            <th className="text-right py-1">Debit</th>
                            <th className="text-right py-1">Kredit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journal.entries.map((e, i) => (
                            <tr key={i}>
                              <td className="py-1 text-[#1A3C34]">{e.account}</td>
                              <td className="py-1 text-right font-mono">{e.debit > 0 ? formatFullCurrency(e.debit) : ''}</td>
                              <td className="py-1 text-right font-mono">{e.credit > 0 ? formatFullCurrency(e.credit) : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="border-t border-[#1A3C34]/10">
                          <tr className="font-medium">
                            <td className="py-2">Total</td>
                            <td className="py-2 text-right font-mono">{formatFullCurrency(journal.total_debit)}</td>
                            <td className="py-2 text-right font-mono">{formatFullCurrency(journal.total_credit)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                )) : (
                  <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-12 text-center text-[#1A3C34]/50">
                    <BookOpen className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Ende pa ditarë të krijuar</p>
                  </div>
                )}
              </div>
            </div>
          );

        case 'documents':
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1A3C34]">Dokumentet në Cloud</h3>
                  <p className="text-sm text-[#1A3C34]/60">{bookkeepingStats?.documents || 0} / {bookkeepingStats?.max_documents === 9999 ? '∞' : bookkeepingStats?.max_documents || 10} dokumente</p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAnalyzeBookkeeping} 
                    disabled={analyzingBookkeeping || bookkeepingDocuments.length === 0}
                    variant="outline"
                    className="border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373]/10"
                    data-testid="analyze-bookkeeping-btn"
                  >
                    {analyzingBookkeeping ? (
                      <><span className="animate-spin mr-2">⏳</span>Duke analizuar...</>
                    ) : (
                      <><Sparkle className="w-4 h-4 mr-2" />Analizo me AI</>
                    )}
                  </Button>
                  <Button onClick={() => setShowDocumentModal(true)} className="bg-[#1A3C34]" data-testid="upload-document-btn">
                    <CloudArrowUp className="w-4 h-4 mr-2" />Ngarko Dokument
                  </Button>
                </div>
              </div>
              <Progress value={bookkeepingStats?.max_documents === 9999 ? 0 : ((bookkeepingStats?.documents || 0) / (bookkeepingStats?.max_documents || 10)) * 100} className="h-2" />
              
              {/* Previous Analyses */}
              {bookkeepingAnalyses.length > 0 && (
                <div className="bg-gradient-to-br from-[#D4A373]/5 to-transparent rounded-xl border border-[#D4A373]/20 p-4">
                  <h4 className="font-medium text-[#1A3C34] mb-3 flex items-center gap-2">
                    <Sparkle className="w-4 h-4 text-[#D4A373]" />
                    Analizat e Fundit
                  </h4>
                  <div className="space-y-2">
                    {bookkeepingAnalyses.slice(0, 3).map(analysis => (
                      <button
                        key={analysis.analysis_id}
                        onClick={() => { setCurrentAnalysis(analysis); setShowAnalysisModal(true); }}
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-white hover:bg-[#F8F9FA] transition-colors text-left"
                      >
                        <div>
                          <p className="text-sm font-medium text-[#1A3C34]">{analysis.documents_analyzed} dokumente të analizuara</p>
                          <p className="text-xs text-[#1A3C34]/50">{new Date(analysis.created_at).toLocaleDateString('sq-AL')}</p>
                        </div>
                        <CaretRight className="w-4 h-4 text-[#1A3C34]/40" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bookkeepingDocuments.map(doc => renderDocumentCard(doc))}
                {bookkeepingDocuments.length === 0 && (
                  <div className="col-span-full bg-white rounded-xl border border-[#1A3C34]/5 p-12 text-center text-[#1A3C34]/50">
                    <CloudArrowUp className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Ende pa dokumente të ngarkuara</p>
                  </div>
                )}
              </div>
            </div>
          );

        case 'trial-balance':
          if (!trialBalance) {
            fetchTrialBalance();
            return <div className="text-center py-8"><Skeleton className="h-64 w-full" /></div>;
          }
          return (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[#1A3C34]">Bilanci i Provës</h3>
                <Badge className={trialBalance.is_balanced ? 'bg-[#2A9D8F]/10 text-[#2A9D8F]' : 'bg-[#E76F51]/10 text-[#E76F51]'}>
                  {trialBalance.is_balanced ? 'I Balancuar' : 'Jo i Balancuar'}
                </Badge>
              </div>
              <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F8F9FA]">
                    <tr>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Llogaria</th>
                      <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Lloji</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Debit</th>
                      <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Kredit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1A3C34]/5">
                    {trialBalance.accounts.map((acc, i) => (
                      <tr key={i} className="hover:bg-[#F8F9FA]">
                        <td className="p-4 font-medium text-[#1A3C34]">{acc.account}</td>
                        <td className="p-4"><span className="px-2 py-1 rounded-full text-xs" style={{ backgroundColor: `${accountTypes[acc.type]?.color}20`, color: accountTypes[acc.type]?.color }}>{accountTypes[acc.type]?.name}</span></td>
                        <td className="p-4 text-right font-mono text-[#1A3C34]">{formatFullCurrency(acc.debit)}</td>
                        <td className="p-4 text-right font-mono text-[#1A3C34]">{formatFullCurrency(acc.credit)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#1A3C34]/5 border-t-2 border-[#1A3C34]/10">
                    <tr>
                      <td colSpan={2} className="p-4 font-semibold text-[#1A3C34]">TOTALI</td>
                      <td className="p-4 text-right font-mono font-bold text-[#1A3C34]">{formatFullCurrency(trialBalance.total_debit)}</td>
                      <td className="p-4 text-right font-mono font-bold text-[#1A3C34]">{formatFullCurrency(trialBalance.total_credit)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );

        default: // overview
          return (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <QuickStat label="Regjistrime Ledger" value={bookkeepingStats?.ledger_entries || 0} icon={Table} color="default" />
                <QuickStat label="Ditarë" value={bookkeepingStats?.journal_entries || 0} icon={BookOpen} color="default" />
                <QuickStat label="Dokumente Cloud" value={bookkeepingStats?.documents || 0} icon={CloudArrowUp} color="default" />
                <QuickStat label="Bilanci" value={formatCurrency(bookkeepingStats?.balance || 0)} icon={Scales} color={bookkeepingStats?.balance >= 0 ? 'success' : 'danger'} />
              </div>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { view: 'ledger', icon: Table, title: 'Libri i Madh', desc: 'Regjistrime debit/kredit' },
                  { view: 'journal', icon: BookOpen, title: 'Ditari', desc: 'Regjistrime të dyfishta' },
                  { view: 'documents', icon: CloudArrowUp, title: 'Dokumente', desc: 'Ruajtje në cloud' },
                  { view: 'trial-balance', icon: Scales, title: 'Bilanc Prove', desc: 'Verifikim llogarish' }
                ].map(item => (
                  <button key={item.view} onClick={() => setBookkeepingView(item.view)} className="bg-white rounded-xl border border-[#1A3C34]/5 p-5 text-left hover:border-[#1A3C34]/15 hover:shadow-md transition-all group">
                    <div className="w-12 h-12 rounded-xl bg-[#1A3C34]/5 flex items-center justify-center mb-3 group-hover:bg-[#1A3C34]/10 transition-colors">
                      <item.icon className="w-6 h-6 text-[#1A3C34]" />
                    </div>
                    <h4 className="font-medium text-[#1A3C34] mb-1">{item.title}</h4>
                    <p className="text-sm text-[#1A3C34]/60">{item.desc}</p>
                  </button>
                ))}
              </div>

              {/* Chart of Accounts */}
              {chartOfAccounts.length > 0 && (
                <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-5">
                  <h3 className="font-semibold text-[#1A3C34] mb-4">Plani i Llogarive</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {chartOfAccounts.map((acc, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#F8F9FA]">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-8 rounded-full" style={{ backgroundColor: accountTypes[acc.type]?.color }} />
                          <div>
                            <p className="font-medium text-[#1A3C34]">{acc.name}</p>
                            <p className="text-xs text-[#1A3C34]/50">{accountTypes[acc.type]?.name}</p>
                          </div>
                        </div>
                        <span className={`font-mono font-medium ${acc.balance >= 0 ? 'text-[#1A3C34]' : 'text-[#E76F51]'}`}>
                          {formatFullCurrency(acc.balance)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[#1A3C34]">Kontabiliteti (Book-Keeping)</h2>
            <p className="text-sm text-[#1A3C34]/60">Menaxhimi i librit të madh, ditarit dhe dokumenteve</p>
          </div>
          {bookkeepingView !== 'overview' && (
            <Button variant="outline" onClick={() => setBookkeepingView('overview')}>
              <CaretLeft className="w-4 h-4 mr-1" /> Kthehu
            </Button>
          )}
        </div>

        {renderBookkeepingContent()}

        {/* Ledger Entry Modal */}
        <Dialog open={showLedgerModal} onOpenChange={setShowLedgerModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Regjistrim i Ri në Libër</DialogTitle>
              <DialogDescription>Shtoni regjistrim debit ose kredit</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Emri i Llogarisë *</Label>
                <Input value={newLedgerEntry.account_name} onChange={(e) => setNewLedgerEntry({...newLedgerEntry, account_name: e.target.value})} placeholder="p.sh. Arka, Banka, Furnitorët" />
              </div>
              <div>
                <Label>Lloji i Llogarisë</Label>
                <Select value={newLedgerEntry.account_type} onValueChange={(v) => setNewLedgerEntry({...newLedgerEntry, account_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asset">Aset</SelectItem>
                    <SelectItem value="liability">Detyrim</SelectItem>
                    <SelectItem value="equity">Kapital</SelectItem>
                    <SelectItem value="revenue">Të Ardhura</SelectItem>
                    <SelectItem value="expense">Shpenzim</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Debit</Label>
                  <Input type="number" value={newLedgerEntry.debit} onChange={(e) => setNewLedgerEntry({...newLedgerEntry, debit: e.target.value})} placeholder="0.00" />
                </div>
                <div>
                  <Label>Kredit</Label>
                  <Input type="number" value={newLedgerEntry.credit} onChange={(e) => setNewLedgerEntry({...newLedgerEntry, credit: e.target.value})} placeholder="0.00" />
                </div>
              </div>
              <div>
                <Label>Përshkrimi</Label>
                <Input value={newLedgerEntry.description} onChange={(e) => setNewLedgerEntry({...newLedgerEntry, description: e.target.value})} placeholder="Përshkrim opsional..." />
              </div>
              <div>
                <Label>Data</Label>
                <Input type="date" value={newLedgerEntry.date} onChange={(e) => setNewLedgerEntry({...newLedgerEntry, date: e.target.value})} />
              </div>
              <Button onClick={handleCreateLedgerEntry} className="w-full bg-[#1A3C34]">Regjistro</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Journal Entry Modal */}
        <Dialog open={showJournalModal} onOpenChange={setShowJournalModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Ditar i Ri</DialogTitle>
              <DialogDescription>Krijoni regjistrim të dyfishtë (debit = kredit)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Përshkrimi *</Label>
                <Input value={newJournalEntry.description} onChange={(e) => setNewJournalEntry({...newJournalEntry, description: e.target.value})} placeholder="p.sh. Pagesë furnitori, Arkëtim klienti" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={newJournalEntry.date} onChange={(e) => setNewJournalEntry({...newJournalEntry, date: e.target.value})} />
                </div>
                <div>
                  <Label>Referencë</Label>
                  <Input value={newJournalEntry.reference} onChange={(e) => setNewJournalEntry({...newJournalEntry, reference: e.target.value})} placeholder="Nr. fature, etj." />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Regjistrimet</Label>
                  <Button size="sm" variant="outline" onClick={addJournalLine}><Plus className="w-4 h-4 mr-1" />Linjë</Button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {newJournalEntry.entries.map((entry, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-[#F8F9FA]">
                      <Input className="flex-1" placeholder="Llogaria" value={entry.account} onChange={(e) => updateJournalLine(i, 'account', e.target.value)} />
                      <Select value={entry.type} onValueChange={(v) => updateJournalLine(i, 'type', v)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asset">Aset</SelectItem>
                          <SelectItem value="liability">Detyrim</SelectItem>
                          <SelectItem value="equity">Kapital</SelectItem>
                          <SelectItem value="revenue">Të Ardh.</SelectItem>
                          <SelectItem value="expense">Shpenz.</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input className="w-24" type="number" placeholder="Debit" value={entry.debit} onChange={(e) => updateJournalLine(i, 'debit', e.target.value)} />
                      <Input className="w-24" type="number" placeholder="Kredit" value={entry.credit} onChange={(e) => updateJournalLine(i, 'credit', e.target.value)} />
                      {newJournalEntry.entries.length > 2 && (
                        <button onClick={() => removeJournalLine(i)} className="p-1.5 hover:bg-[#E76F51]/10 rounded"><X className="w-4 h-4 text-[#E76F51]" /></button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-4 mt-2 text-sm">
                  <span>Debit: <strong className="font-mono">{formatCurrency(newJournalEntry.entries.reduce((s, e) => s + (parseFloat(e.debit) || 0), 0))}</strong></span>
                  <span>Kredit: <strong className="font-mono">{formatCurrency(newJournalEntry.entries.reduce((s, e) => s + (parseFloat(e.credit) || 0), 0))}</strong></span>
                </div>
              </div>
              <Button onClick={handleCreateJournalEntry} className="w-full bg-[#1A3C34]">Krijo Ditar</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Document Modal */}
        <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ngarko Dokument</DialogTitle>
              <DialogDescription>Ruani dokumentet tuaja në cloud ({bookkeepingStats?.documents_remaining || 0} të mbetura)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Emri i Dokumentit *</Label>
                <Input value={newDocument.name} onChange={(e) => setNewDocument({...newDocument, name: e.target.value})} placeholder="p.sh. Fatura-001, Kontrata-XYZ" />
              </div>
              <div>
                <Label>Lloji</Label>
                <Select value={newDocument.document_type} onValueChange={(v) => setNewDocument({...newDocument, document_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Faturë</SelectItem>
                    <SelectItem value="receipt">Faturë Blerje</SelectItem>
                    <SelectItem value="bank_statement">Pasqyrë Bankare</SelectItem>
                    <SelectItem value="contract">Kontratë</SelectItem>
                    <SelectItem value="tax_document">Dokument Tatimor</SelectItem>
                    <SelectItem value="other">Tjetër</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Skedari (Opsional)</Label>
                <div className="mt-1">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#1A3C34]/20 rounded-lg cursor-pointer hover:border-[#1A3C34]/40 transition-colors bg-[#F8F9FA]">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {newDocument.file ? (
                        <>
                          <File className="w-8 h-8 text-[#2A9D8F] mb-2" />
                          <p className="text-sm text-[#1A3C34] font-medium">{newDocument.file.name}</p>
                          <p className="text-xs text-[#1A3C34]/60">{(newDocument.file.size / 1024).toFixed(1)} KB</p>
                        </>
                      ) : (
                        <>
                          <CloudArrowUp className="w-8 h-8 text-[#1A3C34]/40 mb-2" />
                          <p className="text-sm text-[#1A3C34]/70">Kliko për të zgjedhur skedarin</p>
                          <p className="text-xs text-[#1A3C34]/50">PDF, Imazh, Excel (max 10MB)</p>
                        </>
                      )}
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*,.pdf,.xlsx,.xls,.csv,.doc,.docx"
                      onChange={handleFileSelect}
                      data-testid="document-file-input"
                    />
                  </label>
                  {newDocument.file && (
                    <button 
                      onClick={() => setNewDocument({...newDocument, file: null})}
                      className="mt-2 text-xs text-[#E76F51] hover:underline"
                    >
                      Hiq skedarin
                    </button>
                  )}
                </div>
              </div>
              <div>
                <Label>Shënime</Label>
                <Input value={newDocument.notes} onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})} placeholder="Shënime opsionale..." />
              </div>
              <Button onClick={handleCreateDocument} disabled={uploadingDocument} className="w-full bg-[#1A3C34]">
                {uploadingDocument ? (
                  <><span className="animate-spin mr-2">⏳</span>Duke ngarkuar...</>
                ) : (
                  <><CloudArrowUp className="w-4 h-4 mr-2" />Ngarko në Cloud</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* AI Analysis Modal */}
        <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkle className="w-5 h-5 text-[#D4A373]" weight="fill" />
                Analiza AI e Dokumenteve
              </DialogTitle>
              <DialogDescription>Analiza dhe guida e gjeneruar nga inteligjenca artificiale</DialogDescription>
            </DialogHeader>
            {currentAnalysis && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 text-sm text-[#1A3C34]/60">
                    <span>{currentAnalysis.documents_analyzed} dokumente të analizuara</span>
                    <span>{new Date(currentAnalysis.created_at).toLocaleDateString('sq-AL')}</span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#1A3C34]/15 text-[#1A3C34]"
                    onClick={() => handleExportAnalysisPDF(currentAnalysis)}
                    data-testid="export-analysis-pdf-btn"
                  >
                    <DownloadSimple className="w-4 h-4 mr-2" />Shkarko PDF
                  </Button>
                </div>
                
                <div className="bg-gradient-to-br from-[#D4A373]/10 to-[#D4A373]/5 rounded-xl p-4 border border-[#D4A373]/20">
                  <h4 className="font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                    <Sparkle className="w-4 h-4 text-[#D4A373]" />
                    Analiza
                  </h4>
                  <div className="text-sm text-[#1A3C34]/80 whitespace-pre-wrap prose prose-sm max-w-none">
                    {currentAnalysis.insights}
                  </div>
                </div>

                <div className="bg-[#2A9D8F]/5 rounded-xl p-4 border border-[#2A9D8F]/20">
                  <h4 className="font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-[#2A9D8F]" />
                    Guida Hap-pas-Hapi
                  </h4>
                  <div className="text-sm text-[#1A3C34]/80 whitespace-pre-wrap">
                    {currentAnalysis.guide}
                  </div>
                </div>

                {currentAnalysis.recommendations && currentAnalysis.recommendations.length > 0 && (
                  <div className="bg-[#1A3C34]/5 rounded-xl p-4">
                    <h4 className="font-semibold text-[#1A3C34] mb-3">Rekomandimet</h4>
                    <ul className="space-y-2">
                      {currentAnalysis.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#1A3C34]/80">
                          <Check className="w-4 h-4 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  };
  const renderWorkers = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3C34]">Punëtorët</h2>
          <p className="text-sm text-[#1A3C34]/60">Menaxhoni pagat dhe statistikat e punëtorëve</p>
        </div>
        <Button onClick={() => setShowWorkerModal(true)} className="bg-[#1A3C34] text-white">
          <Plus className="w-4 h-4 mr-2" />
          Shto Punëtor
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QuickStat label="Punëtorë Totalë" value={workersStats?.total_workers || 0} icon={Briefcase} color="default" />
        <QuickStat label="Paga Mujore Totale" value={formatCurrency(workersStats?.total_monthly_wages || 0)} icon={CurrencyDollar} color="warning" />
        <QuickStat label="Bonuse Totale" value={formatCurrency(workersStats?.total_bonuses || 0)} icon={Gift} color="success" />
        <QuickStat label="Paga Mesatare" value={formatCurrency(workersStats?.average_wage || 0)} icon={ChartLineUp} color="default" />
      </div>

      {/* Plan Limit Info */}
      <div className="bg-[#1A3C34]/5 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1A3C34]/10 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-[#1A3C34]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1A3C34]">Kuota e Punëtorëve</p>
            <p className="text-xs text-[#1A3C34]/60">
              {workersStats?.total_workers || 0} / {workersStats?.max_workers === 9999 ? '∞' : workersStats?.max_workers || 5} punëtorë
            </p>
          </div>
        </div>
        <Progress value={workersStats?.max_workers === 9999 ? 0 : ((workersStats?.total_workers || 0) / (workersStats?.max_workers || 5)) * 100} className="w-32 h-2" />
      </div>

      {/* Workers Table */}
      <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
        {workers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F8F9FA]">
                <tr>
                  <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Emri</th>
                  <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Roli</th>
                  <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Klienti</th>
                  <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Paga ({selectedCurrency})</th>
                  <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Bonus</th>
                  <th className="text-left p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Statusi</th>
                  <th className="text-right p-4 text-xs font-medium text-[#1A3C34]/60 uppercase">Veprime</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1A3C34]/5">
                {workers.map(worker => {
                  const workerClient = clientsFromDB.find(c => c.client_id === worker.client_id);
                  return (
                    <tr key={worker.worker_id} className="hover:bg-[#F8F9FA]">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-medium text-sm">
                            {worker.name?.charAt(0) || 'P'}
                          </div>
                          <div>
                            <span className="font-medium text-[#1A3C34]">{worker.name}</span>
                            {worker.hire_date && <p className="text-xs text-[#1A3C34]/50">Punësuar: {worker.hire_date}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-[#1A3C34]/70">{worker.role}</td>
                      <td className="p-4">
                        {workerClient ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#1A3C34]/10 text-[#1A3C34]">
                            {workerClient.name}
                          </span>
                        ) : (
                          <span className="text-[#1A3C34]/40 text-xs">Pa caktuar</span>
                        )}
                      </td>
                      <td className="p-4 font-mono text-sm text-[#1A3C34]">{formatFullCurrency(worker.monthly_wage)}</td>
                      <td className="p-4">
                        {worker.bonus > 0 ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-[#2A9D8F]/10 text-[#2A9D8F]">
                            +{formatFullCurrency(worker.bonus)}
                          </span>
                        ) : (
                          <span className="text-[#1A3C34]/40 text-sm">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${worker.is_active ? 'bg-[#2A9D8F]/10 text-[#2A9D8F]' : 'bg-[#E76F51]/10 text-[#E76F51]'}`}>
                          {worker.is_active ? 'Aktiv' : 'Joaktiv'}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setEditingWorker(worker)} className="p-2 hover:bg-[#1A3C34]/5 rounded-lg">
                            <PencilSimple className="w-4 h-4 text-[#1A3C34]/60" />
                          </button>
                          <button onClick={() => handleDeleteWorker(worker.worker_id)} className="p-2 hover:bg-[#E76F51]/5 rounded-lg">
                            <Trash className="w-4 h-4 text-[#E76F51]/60" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={Briefcase} title="Ende pa punëtorë" description="Shtoni punëtorin tuaj të parë për të filluar menaxhimin e pagave" actionLabel="Shto Punëtor" onAction={() => setShowWorkerModal(true)} />
        )}
      </div>

      {/* Add Worker Modal */}
      <Dialog open={showWorkerModal} onOpenChange={setShowWorkerModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Shto Punëtor të Ri</DialogTitle>
            <DialogDescription>Vendosni të dhënat e punëtorit</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Emri i Plotë *</Label>
              <Input value={newWorker.name} onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })} placeholder="p.sh. Arben Hoxha" />
            </div>
            <div>
              <Label>Roli *</Label>
              <Input value={newWorker.role} onChange={(e) => setNewWorker({ ...newWorker, role: e.target.value })} placeholder="p.sh. Kontabilist, Menaxher" />
            </div>
            <div>
              <Label>Klienti (Opsional)</Label>
              <Select value={newWorker.client_id || 'none'} onValueChange={(v) => setNewWorker({ ...newWorker, client_id: v === 'none' ? null : v })}>
                <SelectTrigger><SelectValue placeholder="Zgjidh klientin..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Pa caktuar</SelectItem>
                  {clientsFromDB.map(c => (
                    <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Paga Mujore (USD) *</Label>
                <Input type="number" value={newWorker.monthly_wage} onChange={(e) => setNewWorker({ ...newWorker, monthly_wage: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <Label>Bonus</Label>
                <Input type="number" value={newWorker.bonus} onChange={(e) => setNewWorker({ ...newWorker, bonus: e.target.value })} placeholder="0.00" />
              </div>
            </div>
            <div>
              <Label>Data e Punësimit</Label>
              <Input type="date" value={newWorker.hire_date} onChange={(e) => setNewWorker({ ...newWorker, hire_date: e.target.value })} />
            </div>
            <div>
              <Label>Shënime</Label>
              <Input value={newWorker.notes} onChange={(e) => setNewWorker({ ...newWorker, notes: e.target.value })} placeholder="Shënime shtesë..." />
            </div>
            <Button onClick={handleAddWorker} className="w-full bg-[#1A3C34]">Shto Punëtor</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Worker Modal */}
      <Dialog open={!!editingWorker} onOpenChange={() => setEditingWorker(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifiko Punëtorin</DialogTitle>
            <DialogDescription>Përditësoni të dhënat e punëtorit</DialogDescription>
          </DialogHeader>
          {editingWorker && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Emri i Plotë</Label>
                <Input value={editingWorker.name} onChange={(e) => setEditingWorker({ ...editingWorker, name: e.target.value })} />
              </div>
              <div>
                <Label>Roli</Label>
                <Input value={editingWorker.role} onChange={(e) => setEditingWorker({ ...editingWorker, role: e.target.value })} />
              </div>
              <div>
                <Label>Klienti</Label>
                <Select value={editingWorker.client_id || 'none'} onValueChange={(v) => setEditingWorker({ ...editingWorker, client_id: v === 'none' ? null : v })}>
                  <SelectTrigger><SelectValue placeholder="Zgjidh klientin..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Pa caktuar</SelectItem>
                    {clientsFromDB.map(c => (
                      <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Paga Mujore (USD)</Label>
                  <Input type="number" value={editingWorker.monthly_wage} onChange={(e) => setEditingWorker({ ...editingWorker, monthly_wage: e.target.value })} />
                </div>
                <div>
                  <Label>Bonus</Label>
                  <Input type="number" value={editingWorker.bonus} onChange={(e) => setEditingWorker({ ...editingWorker, bonus: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Data e Punësimit</Label>
                <Input type="date" value={editingWorker.hire_date || ''} onChange={(e) => setEditingWorker({ ...editingWorker, hire_date: e.target.value })} />
              </div>
              <div>
                <Label>Shënime</Label>
                <Input value={editingWorker.notes || ''} onChange={(e) => setEditingWorker({ ...editingWorker, notes: e.target.value })} />
              </div>
              <Button onClick={handleUpdateWorker} className="w-full bg-[#1A3C34]">Ruaj Ndryshimet</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );

  // ===== TEAM VIEW =====
  const renderTeam = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1A3C34]">Ekipi</h2>
          <p className="text-sm text-[#1A3C34]/60">Menaxhoni anëtarët e ekipit dhe qasjen e tyre</p>
        </div>
        <Button onClick={() => setShowTeamModal(true)} className="bg-[#1A3C34] text-white">
          <UserPlus className="w-4 h-4 mr-2" />
          Fto Anëtar
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QuickStat label="Anëtarë Totalë" value={teamStats?.total_members || 0} icon={UsersThree} color="default" />
        <QuickStat label="Anëtarë Aktivë" value={teamStats?.active_members || 0} icon={Check} color="success" />
        <QuickStat label="Ftesa në Pritje" value={teamStats?.pending_invites || 0} icon={Clock} color="warning" />
        <QuickStat label="Vende të Lira" value={teamStats?.max_members === 9999 ? '∞' : teamStats?.remaining_slots || 0} icon={UserCirclePlus} color="default" />
      </div>

      {/* Plan Limit Info */}
      <div className="bg-[#1A3C34]/5 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1A3C34]/10 flex items-center justify-center">
            <UsersThree className="w-5 h-5 text-[#1A3C34]" />
          </div>
          <div>
            <p className="text-sm font-medium text-[#1A3C34]">Kuota e Ekipit</p>
            <p className="text-xs text-[#1A3C34]/60">
              {teamStats?.total_members || 0} / {teamStats?.max_members === 9999 ? '∞' : teamStats?.max_members || 1} anëtarë
            </p>
          </div>
        </div>
        <Progress value={teamStats?.max_members === 9999 ? 0 : ((teamStats?.total_members || 0) / (teamStats?.max_members || 1)) * 100} className="w-32 h-2" />
      </div>

      {/* Role Legend */}
      <div className="bg-white rounded-xl border border-[#1A3C34]/5 p-4">
        <h3 className="font-medium text-[#1A3C34] mb-3">Rolet dhe Lejet</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F8F9FA]">
            <div className="w-8 h-8 rounded-lg bg-[#D4A373]/10 flex items-center justify-center">
              <Crown className="w-4 h-4 text-[#D4A373]" weight="fill" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A3C34]">Admin</p>
              <p className="text-xs text-[#1A3C34]/60">Qasje e plotë, mund të ftojë anëtarë të tjerë</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F8F9FA]">
            <div className="w-8 h-8 rounded-lg bg-[#2A9D8F]/10 flex items-center justify-center">
              <Calculator className="w-4 h-4 text-[#2A9D8F]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A3C34]">Kontabilist</p>
              <p className="text-xs text-[#1A3C34]/60">Menaxhon financat, nuk fton anëtarë</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-[#F8F9FA]">
            <div className="w-8 h-8 rounded-lg bg-[#1A3C34]/10 flex items-center justify-center">
              <Eye className="w-4 h-4 text-[#1A3C34]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A3C34]">Shikues</p>
              <p className="text-xs text-[#1A3C34]/60">Vetëm lexim, pa ndryshime</p>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members List */}
      <div className="bg-white rounded-xl border border-[#1A3C34]/5 overflow-hidden">
        {teamMembers.length > 0 ? (
          <div className="divide-y divide-[#1A3C34]/5">
            {teamMembers.map(member => (
              <div key={member.member_id} className="flex items-center justify-between p-4 hover:bg-[#F8F9FA]">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1A3C34] to-[#2A9D8F] flex items-center justify-center text-white font-medium">
                    {member.name?.charAt(0) || member.email.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-[#1A3C34]">{member.name || member.email}</p>
                      {member.status === 'pending' && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-[#D4A373]/10 text-[#D4A373]">Në Pritje</span>
                      )}
                    </div>
                    <p className="text-sm text-[#1A3C34]/60">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={member.role} onValueChange={(value) => handleUpdateTeamMember(member.member_id, value)}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <span className="flex items-center gap-2">
                          <Crown className="w-4 h-4 text-[#D4A373]" weight="fill" />
                          Admin
                        </span>
                      </SelectItem>
                      <SelectItem value="accountant">
                        <span className="flex items-center gap-2">
                          <Calculator className="w-4 h-4 text-[#2A9D8F]" />
                          Kontabilist
                        </span>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <span className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-[#1A3C34]" />
                          Shikues
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <button onClick={() => handleRemoveTeamMember(member.member_id)} className="p-2 hover:bg-[#E76F51]/5 rounded-lg">
                    <Trash className="w-4 h-4 text-[#E76F51]/60" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={UsersThree} title="Ende pa anëtarë ekipi" description="Ftoni anëtarët e ekipit tuaj për të bashkëpunuar" actionLabel="Fto Anëtar" onAction={() => setShowTeamModal(true)} />
        )}
      </div>

      {/* Invite Modal */}
      <Dialog open={showTeamModal} onOpenChange={setShowTeamModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fto Anëtar të Ri</DialogTitle>
            <DialogDescription>Dërgoni ftesë me email për t'u bashkuar me ekipin tuaj</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Email *</Label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A3C34]/40" />
                <Input value={newTeamMember.email} onChange={(e) => setNewTeamMember({ ...newTeamMember, email: e.target.value })} placeholder="email@shembull.com" className="pl-10" />
              </div>
            </div>
            <div>
              <Label>Roli</Label>
              <Select value={newTeamMember.role} onValueChange={(value) => setNewTeamMember({ ...newTeamMember, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-[#D4A373]" weight="fill" />
                      Admin - Qasje e plotë
                    </span>
                  </SelectItem>
                  <SelectItem value="accountant">
                    <span className="flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-[#2A9D8F]" />
                      Kontabilist - Menaxhon financat
                    </span>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <span className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-[#1A3C34]" />
                      Shikues - Vetëm lexim
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleInviteTeamMember} className="w-full bg-[#1A3C34]">
              <EnvelopeSimple className="w-4 h-4 mr-2" />
              Dërgo Ftesën
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Get current page title
  const getPageTitle = () => {
    const item = navItems.find(n => n.id === activeNav);
    return item?.label || 'Paneli Kryesor';
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className={`fixed left-0 top-0 h-full bg-white border-r border-[#1A3C34]/5 z-50 transition-all duration-300 ${
        sidebarCollapsed ? 'w-16' : 'w-60'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={`p-4 border-b border-[#1A3C34]/5 ${sidebarCollapsed ? 'px-3' : ''}`}>
            <Link to="/dashboard" className="flex items-center gap-3">
              <FinanciALLogo />
              {!sidebarCollapsed && <span className="font-semibold text-[#1A3C34]">FinanciAL</span>}
            </Link>
          </div>

          {/* Currency Selector */}
          {!sidebarCollapsed && (
            <div className="p-3 border-b border-[#1A3C34]/5">
              <button
                onClick={() => setShowCurrencySettings(true)}
                className="w-full flex items-center gap-2 p-2 rounded-lg bg-[#F8F9FA] hover:bg-[#1A3C34]/5 transition-colors"
              >
                <span className="text-lg">{CURRENCIES[selectedCurrency]?.flag}</span>
                <span className="text-sm font-medium text-[#1A3C34]">{selectedCurrency}</span>
                <span className="text-xs text-[#1A3C34]/50 flex-1 text-left">{CURRENCIES[selectedCurrency]?.symbol}</span>
                <CaretDown className="w-4 h-4 text-[#1A3C34]/40" />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
            {navItems.map(item => (
              <NavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                active={activeNav === item.id}
                onClick={() => setActiveNav(item.id)}
                badge={item.badge}
                collapsed={sidebarCollapsed}
              />
            ))}
            
            <div className="pt-4 mt-4 border-t border-[#1A3C34]/5">
              {!sidebarCollapsed && (
                <p className="px-3 text-xs font-medium text-[#1A3C34]/40 uppercase tracking-wider mb-2">Cilësimet</p>
              )}
              <NavItem icon={Gear} label="Cilësimet" active={false} onClick={openSettings} collapsed={sidebarCollapsed} />
              <NavItem icon={CreditCard} label="Faturimi" active={false} onClick={() => navigate('/billing')} collapsed={sidebarCollapsed} />
              <NavItem icon={Question} label="Ndihmë" active={false} onClick={() => {}} collapsed={sidebarCollapsed} />
            </div>
          </nav>

          {/* User Section */}
          {!sidebarCollapsed && (
            <div className="p-3 border-t border-[#1A3C34]/5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A3C34]/5 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-[#1A3C34] flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-[#1A3C34] truncate">{user?.name}</p>
                    </div>
                    <CaretDown className="w-4 h-4 text-[#1A3C34]/40" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={openSettings} data-testid="user-menu-settings">
                    <Gear className="w-4 h-4 mr-2" />
                    Cilësimet
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowCurrencySettings(true)}>
                    <CurrencyCircleDollar className="w-4 h-4 mr-2" />
                    Ndrysho Monedhën
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/billing')}>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Faturimi
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <SignOut className="w-4 h-4 mr-2" />
                    Dil
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-[#1A3C34]/5">
          <div className="flex items-center justify-between h-14 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="p-2 rounded-lg hover:bg-[#1A3C34]/5 transition-colors">
                <List className="w-5 h-5 text-[#1A3C34]/60" />
              </button>
              <h1 className="text-lg font-semibold text-[#1A3C34]">{getPageTitle()}</h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Currency Badge */}
              <button 
                onClick={() => setShowCurrencySettings(true)}
                className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A3C34]/5 hover:bg-[#1A3C34]/10 transition-colors"
              >
                <span>{CURRENCIES[selectedCurrency]?.flag}</span>
                <span className="text-sm font-medium text-[#1A3C34]">{selectedCurrency}</span>
              </button>
              <button className="p-2 rounded-lg hover:bg-[#1A3C34]/5 transition-colors relative">
                <Bell className="w-5 h-5 text-[#1A3C34]/60" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#E76F51] rounded-full" />
              </button>
              <p className="text-sm text-[#1A3C34]/60 hidden lg:block">
                Mirë se u ktheve, <span className="font-medium text-[#1A3C34]">{user?.name?.split(' ')[0]}</span>
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
              </div>
              <Skeleton className="h-80 rounded-xl" />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeNav}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>

      {/* ===== MODALS ===== */}
      
      {/* Add Company Modal */}
      <AddCompanyModal open={showAddCompany} onClose={() => setShowAddCompany(false)} onSuccess={handleCompanyAdded} />

      {/* Document Preview Modal (global) */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 truncate">
              <File className="w-5 h-5 text-[#2A9D8F]" weight="fill" />
              {previewDoc?.name || 'Dokument'}
            </DialogTitle>
            <DialogDescription>{previewDoc?.file_name}</DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {previewLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!previewLoading && previewDoc?.dataUrl && (
              <>
                {previewDoc.file_type?.startsWith('image/') && (
                  <img src={previewDoc.dataUrl} alt={previewDoc.name} className="w-full rounded-lg border border-[#1A3C34]/10" data-testid="preview-image" />
                )}
                {previewDoc.file_type === 'application/pdf' && (
                  <iframe title={previewDoc.name} src={previewDoc.dataUrl} className="w-full h-[70vh] rounded-lg border border-[#1A3C34]/10" data-testid="preview-pdf" />
                )}
                {!previewDoc.file_type?.startsWith('image/') && previewDoc.file_type !== 'application/pdf' && (
                  <div className="text-center py-12 text-[#1A3C34]/60">
                    <File className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="mb-4">Ky lloj skedari nuk mund të shfaqet drejtpërdrejt.</p>
                    <Button
                      className="bg-[#1A3C34]"
                      onClick={() => { const a = document.createElement('a'); a.href = previewDoc.dataUrl; a.download = previewDoc.file_name || previewDoc.name; a.click(); }}
                    >
                      <DownloadSimple className="w-4 h-4 mr-2" />Shkarko Skedarin
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Currency Settings Modal */}

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="settings-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gear className="w-5 h-5 text-[#1A3C34]" />
              Cilësimet
            </DialogTitle>
            <DialogDescription>Menaxhoni llogarinë, pamjen dhe preferencat tuaja</DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-2">
            {/* Account Management */}
            <section>
              <h4 className="text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                <UserCircle className="w-4 h-4 text-[#2A9D8F]" /> Menaxhimi i Llogarisë
              </h4>
              <div className="space-y-3 bg-[#F8F9FA] rounded-xl p-4">
                <div>
                  <Label className="text-xs text-[#1A3C34]/60">Emri i Plotë</Label>
                  <Input value={settingsName} onChange={(e) => setSettingsName(e.target.value)} className="mt-1" data-testid="settings-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-[#1A3C34]/60">Email</Label>
                  <Input value={user?.email || ''} disabled className="mt-1 opacity-70" />
                </div>
                <div className="flex items-center justify-between text-xs text-[#1A3C34]/60">
                  <span>Plani aktual: <span className="font-medium text-[#1A3C34] capitalize">{user?.subscription_tier || 'free'}</span></span>
                  <button onClick={() => { setShowSettingsModal(false); navigate('/billing'); }} className="text-[#2A9D8F] font-medium hover:underline" data-testid="settings-manage-plan">Menaxho Abonimin</button>
                </div>
                <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full bg-[#1A3C34]" data-testid="settings-save-profile">
                  {savingProfile ? 'Duke ruajtur...' : 'Ruaj Ndryshimet'}
                </Button>
              </div>
            </section>

            {/* Appearance */}
            <section>
              <h4 className="text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                <Palette className="w-4 h-4 text-[#D4A373]" /> Pamja
              </h4>
              <div className="flex items-center justify-between bg-[#F8F9FA] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  {darkMode ? <Moon className="w-5 h-5 text-[#1A3C34]" weight="fill" /> : <Sun className="w-5 h-5 text-[#D4A373]" weight="fill" />}
                  <div>
                    <p className="text-sm font-medium text-[#1A3C34]">Modaliteti i Errët</p>
                    <p className="text-xs text-[#1A3C34]/60">Aktivizo temën e errët për sytë</p>
                  </div>
                </div>
                <Switch checked={darkMode} onCheckedChange={setDarkMode} data-testid="settings-dark-mode-toggle" />
              </div>
            </section>

            {/* Notifications */}
            <section>
              <h4 className="text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#2A9D8F]" /> Njoftimet
              </h4>
              <div className="space-y-2 bg-[#F8F9FA] rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1A3C34]/80">Njoftime me Email</span>
                  <Switch checked={notifEmail} onCheckedChange={(v) => { setNotifEmail(v); localStorage.setItem('financial_notif_email', String(v)); }} data-testid="settings-notif-email" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1A3C34]/80">Lajme & Përditësime Produkti</span>
                  <Switch checked={notifProduct} onCheckedChange={(v) => { setNotifProduct(v); localStorage.setItem('financial_notif_product', String(v)); }} data-testid="settings-notif-product" />
                </div>
              </div>
            </section>

            {/* Preferences */}
            <section>
              <h4 className="text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#1A3C34]" /> Preferencat
              </h4>
              <div className="space-y-2 bg-[#F8F9FA] rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#1A3C34]/80">Gjuha</span>
                  <span className="text-sm font-medium text-[#1A3C34]">Shqip</span>
                </div>
                <button onClick={() => { setShowSettingsModal(false); setShowCurrencySettings(true); }} className="w-full flex items-center justify-between pt-1" data-testid="settings-currency">
                  <span className="text-sm text-[#1A3C34]/80">Monedha</span>
                  <span className="text-sm font-medium text-[#2A9D8F] flex items-center gap-1">{CURRENCIES[selectedCurrency]?.flag} {selectedCurrency} <CaretRight className="w-4 h-4" /></span>
                </button>
              </div>
            </section>

            {/* Change Password */}
            <section>
              <h4 className="text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                <LockKey className="w-4 h-4 text-[#E76F51]" /> Siguria
              </h4>
              <div className="space-y-3 bg-[#F8F9FA] rounded-xl p-4">
                <Input type="password" placeholder="Fjalëkalimi aktual" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="settings-current-password" />
                <Input type="password" placeholder="Fjalëkalimi i ri" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} data-testid="settings-new-password" />
                <Input type="password" placeholder="Konfirmo fjalëkalimin e ri" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="settings-confirm-password" />
                <Button onClick={handleChangePassword} disabled={changingPw} variant="outline" className="w-full border-[#1A3C34]/15" data-testid="settings-change-password-btn">
                  {changingPw ? 'Duke ndryshuar...' : 'Ndrysho Fjalëkalimin'}
                </Button>
              </div>
            </section>

            {/* 2FA Section */}
            <section>
              <h4 className="text-sm font-semibold text-[#1A3C34] mb-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#2A9D8F]" /> Autentifikimi 2FA
              </h4>
              <div className="space-y-3 bg-[#F8F9FA] rounded-xl p-4">
                {showTwoFAVerify ? (
                  <>
                    <p className="text-sm text-[#1A3C34]/70">
                      Kodi u dërgua në email tuaj. Vendosni kodin 6-shifror:
                    </p>
                    <Input
                      type="text"
                      placeholder="000000"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="text-center text-xl tracking-[0.3em] font-mono"
                      maxLength={6}
                      data-testid="settings-2fa-code"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleVerify2FAToggle}
                        disabled={twoFALoading || twoFACode.length !== 6}
                        className="flex-1 bg-[#1A3C34]"
                        data-testid="settings-2fa-verify-btn"
                      >
                        {twoFALoading ? 'Duke verifikuar...' : 'Verifiko'}
                      </Button>
                      <Button
                        onClick={() => { setShowTwoFAVerify(false); setTwoFACode(''); }}
                        variant="outline"
                        className="flex-1"
                      >
                        Anulo
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${twoFAEnabled ? 'bg-[#2A9D8F]/10' : 'bg-[#1A3C34]/5'}`}>
                        <ShieldCheck className={`w-5 h-5 ${twoFAEnabled ? 'text-[#2A9D8F]' : 'text-[#1A3C34]/40'}`} weight={twoFAEnabled ? 'fill' : 'regular'} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1A3C34]">
                          {twoFAEnabled ? '2FA e Aktivizuar' : '2FA e Çaktivizuar'}
                        </p>
                        <p className="text-xs text-[#1A3C34]/60">
                          {twoFAEnabled ? 'Kodi dërgohet në email gjatë hyrjes' : 'Aktivizo për siguri shtesë'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={twoFAEnabled}
                      onCheckedChange={handleToggle2FA}
                      disabled={twoFALoading}
                      data-testid="settings-2fa-toggle"
                    />
                  </div>
                )}
              </div>
            </section>

            {/* Admin Panel Link */}
            {isAdmin && (
              <section>
                <button
                  onClick={() => { setShowSettingsModal(false); navigate('/admin'); }}
                  className="w-full flex items-center justify-between bg-[#D4A373]/10 rounded-xl p-4 hover:bg-[#D4A373]/20 transition-colors"
                  data-testid="settings-admin-link"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#D4A373]/20 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-[#D4A373]" weight="fill" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-[#1A3C34]">Paneli i Administratorit</p>
                      <p className="text-xs text-[#1A3C34]/60">Menaxho çelësat dhe përdoruesit</p>
                    </div>
                  </div>
                  <CaretRight className="w-5 h-5 text-[#D4A373]" />
                </button>
              </section>
            )}

            {/* Logout */}
            <Button onClick={handleLogout} variant="outline" className="w-full border-[#E76F51]/30 text-[#E76F51] hover:bg-[#E76F51]/10" data-testid="settings-logout-btn">
              <SignOut className="w-4 h-4 mr-2" /> Dil nga Llogaria
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCurrencySettings} onOpenChange={setShowCurrencySettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Zgjidhni Monedhën</DialogTitle>
            <DialogDescription>Zgjidhni monedhën kryesore për aplikacionin</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 pt-4 max-h-80 overflow-y-auto">
            {Object.entries(CURRENCIES).map(([code, info]) => (
              <button
                key={code}
                onClick={() => {
                  handleCurrencyChange(code);
                  setShowCurrencySettings(false);
                }}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  selectedCurrency === code 
                    ? 'border-[#1A3C34] bg-[#1A3C34]/5' 
                    : 'border-[#1A3C34]/10 hover:border-[#1A3C34]/30'
                }`}
              >
                <span className="text-xl">{info.flag}</span>
                <div className="text-left">
                  <p className="font-medium text-[#1A3C34]">{code}</p>
                  <p className="text-xs text-[#1A3C34]/50">{info.symbol}</p>
                </div>
                {selectedCurrency === code && (
                  <Check className="w-4 h-4 text-[#2A9D8F] ml-auto" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Krijo Faturë të Re</DialogTitle>
            <DialogDescription>Plotësoni detajet e faturës</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Klienti</Label>
              <Select value={newInvoice.client} onValueChange={(v) => setNewInvoice({...newInvoice, client: v})}>
                <SelectTrigger><SelectValue placeholder="Zgjidhni klientin" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shuma ({CURRENCIES[selectedCurrency]?.symbol})</Label>
              <Input type="number" placeholder="0.00" value={newInvoice.amount} onChange={(e) => setNewInvoice({...newInvoice, amount: e.target.value})} />
            </div>
            <div>
              <Label>Afati i Pagesës</Label>
              <Input type="date" value={newInvoice.dueDate} onChange={(e) => setNewInvoice({...newInvoice, dueDate: e.target.value})} />
            </div>
            <Button onClick={handleAddInvoice} className="w-full bg-[#1A3C34] text-white">Krijo Faturën</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Regjistro Shpenzim</DialogTitle>
            <DialogDescription>Shtoni një shpenzim të ri</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Përshkrimi</Label>
              <Input placeholder="p.sh. Furnizime zyre" value={newExpense.description} onChange={(e) => setNewExpense({...newExpense, description: e.target.value})} />
            </div>
            <div>
              <Label>Kategoria</Label>
              <Select value={newExpense.category} onValueChange={(v) => setNewExpense({...newExpense, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="supplies">Furnizime</SelectItem>
                  <SelectItem value="travel">Udhëtime</SelectItem>
                  <SelectItem value="meals">Ushqim</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="utilities">Shërbime</SelectItem>
                  <SelectItem value="other">Të tjera</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shuma ({CURRENCIES[selectedCurrency]?.symbol})</Label>
              <Input type="number" placeholder="0.00" value={newExpense.amount} onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})} />
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={newExpense.date} onChange={(e) => setNewExpense({...newExpense, date: e.target.value})} />
            </div>
            <Button onClick={handleAddExpense} className="w-full bg-[#1A3C34] text-white">Regjistro Shpenzimin</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Transaction Modal */}
      <Dialog open={showTransactionModal} onOpenChange={setShowTransactionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Shto Transaksion</DialogTitle>
            <DialogDescription>Regjistroni një transaksion të ri</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Përshkrimi</Label>
              <Input placeholder="p.sh. Pagesa nga klienti" value={newTransaction.description} onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})} />
            </div>
            <div>
              <Label>Lloji</Label>
              <Select value={newTransaction.type} onValueChange={(v) => setNewTransaction({...newTransaction, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">Hyrje (Të ardhura)</SelectItem>
                  <SelectItem value="expense">Dalje (Shpenzim)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Shuma ({CURRENCIES[selectedCurrency]?.symbol})</Label>
              <Input type="number" placeholder="0.00" value={newTransaction.amount} onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})} />
            </div>
            <div>
              <Label>Llogaria</Label>
              <Select value={newTransaction.account} onValueChange={(v) => setNewTransaction({...newTransaction, account: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bank Account">Llogaria Bankare</SelectItem>
                  <SelectItem value="Credit Card">Kartë Krediti</SelectItem>
                  <SelectItem value="Cash">Para në Dorë</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleAddTransaction} className="w-full bg-[#1A3C34] text-white">Regjistro Transaksionin</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
